import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

export type Lang = 'en' | 'da' | 'de'

export const LANGS: { code: Lang; label: string; flag: string }[] = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'da', label: 'Dansk', flag: '🇩🇰' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
]

const LOCALES: Record<Lang, string> = { en: 'en-GB', da: 'da-DK', de: 'de-DE' }

type Dict = Record<string, any>

const en: Dict = {
  nav: { features: 'Features', how: 'How it works', discover: 'Discover', myHorses: 'My Horses', myFarm: 'My Livery', signIn: 'Sign in', getStarted: 'Get started', settings: 'Settings', billing: 'Billing', signOut: 'Sign out', spaceSwitch: 'Switch between your horses and your yard', menu: 'Menu', account: 'Account', tasks: 'Tasks', yard: 'Yard', home: 'Home' },
  features: {
    title: 'Everything HorseSheli does',
    subtitle: 'One account, two apps — for the owners who keep the records and the yards that board them.',
    cta: 'Start free',
    sections: [
      { icon: '🔑', title: 'Accounts & access', items: ['Sign up & sign in', 'Email verification', 'Forgot / reset password', 'Choose your role at sign-up', 'Be a horse owner, a yard, or both', 'Role-aware navigation', 'Bot & disposable-email protection', '90-day free trial'] },
      { icon: '👤', title: 'Owner profile', items: ['Name & avatar', 'About, country & town', 'A “first horse memory”', 'Storage meter', 'Light / dark theme', 'English, Danish & German'] },
      { icon: '🐎', title: 'The horse record', items: ['Create, edit & delete horses', 'Breed, gender, age, height, colour, discipline', 'Microchip & passport (always private)', 'Profile photo', 'Up to 3 horses free'] },
      { icon: '🩺', title: 'Medical book', items: ['A private timeline of care', '11 record types', 'Vet, date & notes', 'Attach vet reports & x-rays', 'Yours alone unless you share it'] },
      { icon: '🔔', title: 'Reminders', items: ['Recurring farrier, vaccination, worming & more', 'Every 1–4 months', 'Mark done — it rolls forward', 'Email when care is due'] },
      { icon: '📄', title: 'Documents', items: ['Upload PDFs, images & files', 'Download any time', '5 free per horse, 50 on premium'] },
      { icon: '📷', title: 'Photos & news', items: ['Photo gallery (Premium)', 'Set a profile photo', 'News posts with images (Premium)', 'Every image checked automatically'] },
      { icon: '🌐', title: 'Social & discovery', items: ['Make a horse public (Premium)', 'Choose what’s visible', 'Country, town or fully private', 'Discover & filter public horses', 'Follow horses & get a feed (Premium)'] },
      { icon: '🏡', title: 'For yards', items: ['Create your yard', 'Address, postcode & emergency contact', 'One dashboard for every horse', 'Livery roster with contacts', 'Microchip & passport at a glance', 'Yard-wide reminders', 'Staff logins — managers & grooms', 'Daily task list your grooms tick off'] },
      { icon: '🧾', title: 'Livery invoicing', items: ['Set your DIY / part / full livery prices', 'Raise an invoice in two taps', 'Auto-build it from each horse’s package', 'Email it to the owner', 'Mark paid & keep a clean history'] },
      { icon: '✉️', title: 'Invites', items: ['Invite liveries & staff by email or link', 'Choose their role — manager, groom or livery', 'See pending & accepted', 'Revoke any time', 'Safe — tied to the invited email'] },
      { icon: '📍', title: 'Livery', items: ['Join yards by invitation', 'Place each horse at a yard', 'Move between yards freely', 'Keep your horse at a yard you own', 'Hide anything you like from the yard'] },
      { icon: '💬', title: 'Messages', items: ['Message your yard', 'Set a category & priority', 'Attach a horse', 'Yards get an inbox'] },
      { icon: '💳', title: 'Plans', items: ['90-day free trial', 'Horse-owner premium', 'Yards — £1.99 a month, everything included', 'Card payments by Stripe'] },
      { icon: '🛡️', title: 'Safety & privacy', items: ['Image moderation on every upload', 'Your medical records stay private', 'Bank-grade auth & encryption', 'Hosted in the EU (Dublin)'] },
    ],
  },
  farm: {
    onboard: { badge: '🏡 For yards & stables', title: 'Set up your yard', subtitle: 'Your owners keep their horses’ records — you get one clear view of the whole yard.', name: 'Yard / stable name', namePlaceholder: 'e.g. Willowbrook Stables', about: 'About the yard', country: 'Country', city: 'Town / city', cta: 'Start 3-month free trial' },
    dash: { title: 'Yard dashboard', trialBanner: 'Yard trial — {days} days left', trialCta: 'Manage subscription', lockedBanner: 'Your yard trial has ended — subscribe to keep running your yard.', roster: 'Horses on the yard', rosterEmpty: 'No horses on the yard yet', rosterEmptyBody: 'Invite your liveries — once they accept and place a horse here, it shows up.', invite: 'Invite someone', yardReminders: 'Coming up across the yard', noReminders: 'Nothing due soon ✅', members: '{n} people', horsesCount: '{n} horses', dueSoon: '{n} due soon', owner: 'Owner', ownersLabel: 'People', compliance: 'Coming up', allGood: 'Up to date', view: 'View' },
    invites: { title: 'Invite to the yard', email: 'Their email', emailPlaceholder: 'name@email.com', name: 'Their name (optional)', role: 'They are a…', send: 'Send invite', sending: 'Sending…', copyLink: 'Copy link', copied: 'Copied!', pending: 'Pending', accepted: 'Joined', revoke: 'Revoke', none: 'No invites yet.', note: 'They get an email with a link — or copy it and send it yourself.' },
    roles: { owner: 'Yard owner', manager: 'Manager', groom: 'Groom / staff', boarder: 'Livery (horse owner)' },
    horse: { back: '← Back to yard', owner: 'Owner', medical: 'Medical book', documents: 'Documents', reminders: 'Reminders', hidden: 'Hidden by owner', noMedical: 'No medical records shared.', noDocuments: 'No documents shared.', noReminders: 'No reminders shared.', nextDue: 'Next due', download: 'Download', location: 'Location on yard' },
    billing: { title: 'Yard subscription', trialBody: 'You’re on the free trial — {days} days left (until {date}).', lockedBody: 'Your yard trial has ended. Subscribe to keep running your yard.', perMonth: '/month', price: '£1.99 / month', feat1: 'The whole yard: roster, staff logins, daily tasks', feat2: 'Livery invoicing — raise, send & track', feat3: 'Unlimited horses, liveries & grooms', subscribe: 'Add payment & subscribe', activeTitle: 'Subscription active', activeBody: 'Thanks — your yard is fully active.', back: 'Back to dashboard', mockNote: 'Mock checkout — no card is charged in this preview.' },
    packages: { full: 'Full livery', part: 'Part livery', diy: 'DIY livery', grass: 'Grass livery', schooling: 'Schooling livery', retirement: 'Retirement livery', other: 'Other', label: 'Livery package', none: 'No package set', prices: 'Livery package prices', pricesHint: 'Monthly price for each package you offer — used to build invoices automatically. Leave blank for packages you don’t offer.', perMonthShort: '/mo' },
    tasks: {
      title: 'Daily tasks', today: 'Today', add: '+ Add task', addTitle: 'Add a task', taskName: 'What needs doing?', taskPlaceholder: 'e.g. Bring in the field horses, PM feeds', notes: 'Notes (optional)', forHorse: 'For a horse (optional)', wholeYard: 'Whole yard', assignTo: 'Assign to (optional)', anyone: 'Anyone',
      empty: 'No tasks for this day', emptyBody: 'Add the day’s jobs — feeds, turnout, rugs, meds — and the team ticks them off.',
      copyYesterday: 'Copy yesterday’s list', copied: '{n} tasks copied', done: 'Done', todo: 'To do', doneBy: 'Done by {name}', progress: '{done} of {total} done', allDone: 'All done for the day 🎉', deleteTask: 'Delete task',
    },
    members: {
      title: 'People on the yard', staff: 'Staff', liveries: 'Liveries', horsesHere: '{n} horse(s) here', joined: 'Joined {date}', role: 'Role', changeRole: 'Change role', remove: 'Remove from yard', removeConfirm: 'Remove {name} from the yard? Their horses will leave the yard too.', empty: 'No one has joined yet', emptyBody: 'Invite your liveries and staff — they appear here once they accept.',
    },
    invoices: {
      title: 'Invoices', myTitle: 'My invoices', raise: '+ New invoice', raiseTitle: 'New invoice', billTo: 'Bill to', autoFill: 'Build from livery packages', autoFillHint: 'Adds a line for each of their horses using your package prices.', period: 'Period (e.g. August 2026)', dueDate: 'Due date', notes: 'Notes (optional)', line: 'Line', lineDesc: 'Description', amount: 'Amount (£)', addLine: '+ Add line', total: 'Total',
      create: 'Create draft', creating: 'Creating…', send: 'Send to {name}', sendShort: 'Send', markPaid: 'Mark paid', voidIt: 'Void', edit: 'Edit',
      empty: 'No invoices yet', emptyBody: 'Set your package prices, then raise your first invoice in two taps.', emptyMine: 'No invoices yet — when your yard bills you, it shows here.',
      st: { draft: 'Draft', sent: 'Sent', paid: 'Paid', void: 'Void' },
      payNote: 'Pay by bank transfer, or however your yard prefers — they’ll mark it paid.', sentOn: 'Sent {date}', paidOn: 'Paid {date}', due: 'Due {date}',
      noPrices: 'Set your livery package prices first (Yard details), or add lines by hand.',
    },
    uk: { editYard: 'Yard details', addressLine: 'Address', postcode: 'Postcode', phone: 'Yard phone', emergencyName: 'Emergency contact', emergencyPhone: 'Emergency phone', contactCard: 'Yard contact', emergency: 'Emergency', microchip: 'Chip', passport: 'Passport', save: 'Save yard details', saved: 'Yard details saved' },
    my: { title: 'My livery', subtitle: 'Yards you belong to — and where each of your horses lives.', joinedHeading: 'Your yards', joinedHint: 'Your own yard shows here automatically. Others appear once they invite you and you accept.', horsesHeading: 'Where your horses live', placeHint: 'Place each horse at one of your yards.', stabledAt: 'Stabled at', notStabled: 'Not at a yard', visibleToYard: 'Shared with the yard', none: 'You’re not on a yard yet', noneBody: 'When a yard invites you, you can accept it here and place your horses.', member: 'You’re a member of {farm}.', board: 'Keep my horse here', boarded: 'Stabled here ✓', unboard: 'Remove', shareTitle: 'What {farm} can see', shareBody: 'Your yard sees these by default. Untick anything you’d rather keep private.', catMedical: 'Medical book', catDocuments: 'Documents', catReminders: 'Reminders', catInfo: 'Details (breed, age…)', yourYard: 'Your yard', stableLocation: 'Where on the yard', stableLocationPlaceholder: 'e.g. Box 4, West field', peopleHeading: 'Who’s on the yard', peopleHint: 'Others who stable or work here.' },
    accept: { title: 'Join {farm} on HorseSheli', heading: 'Join a yard on HorseSheli', invitedYou: '{farm} invited you', body: 'Accept to connect with the yard. You keep full control of your horses and can hide anything you like.', cta: 'Accept invitation', busy: 'Joining…', move: 'Move here', moveNote: 'You’re already at a yard — accepting moves you (and your horses) here.', signin: 'Sign in or create a free account to accept this invitation.', doneTitle: 'You’ve joined {farm} 🎉', doneBody: 'Board a horse to share it with the yard.', invalid: 'Invitation not found', invalidBody: 'This link may have been revoked or already used.', goApp: 'Go to my horses' },
    switchHorses: 'My horses', switchFarm: 'My yard',
  },
  tickets: {
    title: 'Messages', dashTitle: 'Messages from your liveries',
    open: 'Open', resolved: 'Sorted', all: 'All', openCount: '{n} open',
    raise: 'Message the yard',
    noneOwner: 'No messages yet — when a livery sends one, it appears here.',
    noneMine: 'You haven’t sent the yard any messages yet.',
    viewAll: 'View all messages',
    subject: 'Subject', subjectPlaceholder: 'e.g. Extra hay for Comet',
    message: 'Details', messagePlaceholder: 'What do you need?',
    yard: 'Yard', category: 'Category', priority: 'Priority', horse: 'About a horse', horseNone: 'General (not horse-specific)',
    submit: 'Send to the yard', sending: 'Sending…',
    markResolved: 'Mark as sorted', reopen: 'Reopen', by: 'From', about: 'about',
    cat: { general: 'General', care: 'Care & feed', facilities: 'Facilities', health: 'Health', billing: 'Billing' },
    prio: { normal: 'Normal', urgent: 'Urgent' },
    st: { open: 'Open', resolved: 'Resolved' },
  },
  footer: { tagline: 'A calmer way to keep every horse — and every yard — organised.', privacy: 'Privacy', terms: 'Terms' },
  crash: { title: 'Something went wrong', body: 'That page hit a snag. Try again, or reload — your data is safe.', retry: 'Try again', reload: 'Reload' },
  update: { available: 'A new version is available', reload: 'Reload', later: 'Dismiss' },
  units: { yrs: 'yrs', hh: 'hh' },
  common: { save: 'Save', delete: 'Delete', cancel: 'Cancel', close: 'Close' },
  confirm: { deleteTitle: 'Delete this?', deleteBody: 'This can’t be undone.' },
  errors: {
    rateLimited: 'Too many requests — please wait a moment and try again.', inviteEmailMismatch: 'This invite was sent to a different email address. Sign in with the invited email to accept.', invalidInput: 'That didn’t look right — please check the form and try again.',
    generic: 'Something went wrong. Please try again.',
    trialExpired: 'Your free trial has ended — subscribe to keep using this.',
    imageRejected: 'That image was blocked by our content check.',
    quota: 'You’ve reached your storage limit.',
    docLimit: 'You’ve reached the document limit for this horse.',
    horseLimit: 'You’ve reached the horse limit on your plan.',
    notMember: 'You need to join this yard first.',
    unauthorized: 'Please sign in again.',
    required: 'Please fill in: {fields}',
  },
  // Friendly rewrites of raw Cognito auth errors (matched by substring).
  authErrors: {
    incorrect: 'That email or password doesn’t match — have another go.',
    notConfirmed: 'Your email isn’t verified yet — check your inbox for the code.',
    exists: 'There’s already an account with this email. Try signing in instead.',
    policy: 'Passwords need at least 10 characters, with a capital letter, a small letter and a number.',
    codeMismatch: 'That code doesn’t match — check the email and try again.',
    expired: 'That code has expired — we can send you a fresh one.',
    attempts: 'Too many tries — wait a few minutes and try again.',
    notFound: 'We can’t find an account with that email.',
    captcha: 'The security check didn’t pass — please try again.',
    generic: 'That didn’t work — please check your details and try again.',
  },
  gender: { Mare: 'Mare', Stallion: 'Stallion', Gelding: 'Gelding' },
  status: { active: 'active', sold: 'sold', retired: 'retired', deceased: 'deceased' },
  colour: { Bay: 'Bay', 'Dark Bay': 'Dark Bay', Chestnut: 'Chestnut', Grey: 'Grey', Dun: 'Dun' },
  discipline: { Eventing: 'Eventing', Dressage: 'Dressage', Showing: 'Showing' },
  medicalCategory: {
    vaccination: 'Vaccination', deworming: 'Worming', dental: 'Dental', farrier: 'Farrier', vet_exam: 'Vet check',
    bloodwork: 'Bloodwork', injury: 'Injury', medication: 'Medication', allergy: 'Allergy', weight_condition: 'Weight & condition', other: 'Other',
  },
  landing: {
    heroBadge: '🐎 For horse owners, by horse people',
    heroTitleA: 'Your horse’s whole life,',
    heroTitleB: 'beautifully organised.',
    heroSubtitle: 'Records, documents, medical history and reminders for every horse you own — private by default, shareable when you choose. No spreadsheets. No shoeboxes of paper.',
    getStarted: 'Start free trial →',
    seePublic: 'Discover horses',
    twoDoor: { title: 'Two ways in', horseTitle: 'I’m a horse owner', horseBody: 'Keep your horse’s profile, photos, medical book and reminders in one calm place. Free to start.', horseCta: 'Start free →', farmTitle: 'I run a livery yard', farmBody: 'Run the whole yard from your phone — daily tasks for your grooms, every horse at a glance, and livery invoicing built in. £1.99 a month after a 3-month free trial.', farmCta: 'Start your yard →' },
    trialNote: '3 months free — no card required to start.',
    features: {
      title: 'Everything a horse needs — minus the admin',
      subtitle: 'HorseSheli is a management tool first. Calm, clear, and built so a non-technical owner can use it with no tutorial.',
      items: [
        { title: 'Every record in one place', body: 'Info, photos, documents, medical history, news — each horse organised into clean sections you can reach in a tap.' },
        { title: 'Never miss a date again', body: 'Farrier, worming, vaccinations, dental — gentle email reminders before things fall due.' },
        { title: 'You decide what’s shared', body: 'Profiles are private by default. Make a horse — or just its photos — public when you want to, section by section.' },
        { title: 'Documents that never get lost', body: 'Passport, registration, insurance, certificates — uploaded once, safe forever, ready when you need them.' },
        { title: 'A medical history that travels', body: 'Vaccinations, treatments, vet visits in one timeline. Share it with a vet in seconds.' },
        { title: 'Connect, not scroll', body: 'Follow horses you love, share a profile link, see their news. A network — without the noise of a feed.' },
      ],
    },
    how: {
      title: 'Up and running in minutes',
      steps: [
        { title: 'Add your horse', body: 'Name, photo, breed, the basics. Takes a minute.' },
        { title: 'Fill in the life', body: 'Upload documents, log medical and care. Or do it bit by bit.' },
        { title: 'Share if you want', body: 'Keep it private, or publish a beautiful profile to share.' },
      ],
    },
    privacy: {
      badge: '🔒 Privacy is the default',
      title: 'Private until you say otherwise',
      body: 'Medical records, documents and reminders are private to you, full stop. Photos, basic info and news can be made public, section by section — so you share exactly what you want and nothing more.',
      bullets: ['Each horse: public or private', 'Each section: independently controlled', 'Medical & documents: always private', 'Share a clean public link in one tap'],
    },
    cta: { title: 'Have a look around', body: 'Start your free 3-month trial and tell us what would make this genuinely useful for your yard.', button: 'Start free trial →' },
  },
  auth: {
    magic: {"or": "or", "cta": "Email me a sign-in link", "sent": "Check your email — we've sent you a link that signs you straight in. It lasts 15 minutes.", "needEmail": "Enter your email address first.", "failedTitle": "That link didn't work", "expired": "Sign-in links last 15 minutes and work once. Ask for a fresh one and you'll be straight in.", "badLink": "That link looks incomplete. Ask for a new one below.", "tryAgain": "Send me a new link"},
    email: 'Email', password: 'Password', newPassword: 'New password', code: 'Verification code',
    agreePre: 'By creating an account you agree to the', agreeTerms: 'Terms of Service', agreeAnd: 'and the', agreePrivacy: 'Privacy Policy', agreePost: '.',
    chooseRole: 'First things first — how will you use HorseSheli?',
    subtitleHorse: 'Free for your horses’ records, photos and reminders.',
    subtitleFarm: 'Start your yard’s 3-month free trial — no card needed.',
    roleHorseDesc: 'My horses’ records, medical book and reminders — and the yard they live at.',
    roleFarmDesc: 'Run the yard: liveries and staff, daily tasks, and livery invoicing.',
    passwordHint: 'At least 10 characters, with upper- and lower-case letters and a number.',
    noAccount: 'No account yet?', haveAccount: 'Already have an account?', forgotLink: 'Forgot your password?',
    resend: 'Resend code', backToSignin: 'Back to sign in', backHome: 'Back home',
    signin: { title: 'Welcome back', subtitle: 'Sign in to your HorseSheli account.', cta: 'Sign in' },
    signup: { title: 'Create your account', subtitle: 'Start your free 3-month trial.', cta: 'Sign up' },
    confirm: { title: 'Check your email', subtitle: 'Enter the code we just sent you.', cta: 'Confirm' },
    forgot: { title: 'Reset your password', subtitle: 'We’ll email you a reset code.', cta: 'Send code' },
    reset: { title: 'Choose a new password', subtitle: 'Enter the code and your new password.', cta: 'Reset password' },
  },
  onboarding: {
    badge: 'Welcome', title: 'Let’s set up your profile', subtitle: 'A few details so other owners know who you are. You can change these any time.',
    addPhoto: 'Add a photo', name: 'Your name', about: 'About you', aboutPlaceholder: 'Lifelong rider, amateur dressage…',
    country: 'Country', city: 'City', memory: 'A favourite horse memory (optional)', memoryPlaceholder: 'The first pony you ever rode…', cta: 'Continue to my horses →',
  },
  dashboard: {
    trialBanner: 'You’re on the free trial — {days} days left.', trialCta: 'Manage plan',
    welcome: 'Welcome back, {name}', addHorse: '+ Add horse',
    stats: { horses: 'Horses', public: 'Public profiles', storage: 'Storage used' },
    myHorses: 'My Horses', comingSoon: 'More horses — coming soon',
    empty: { title: 'No horses yet', body: 'Add your first horse to start building its profile, medical book and more.' },
  },
  horse: {
    "access": {"title": "People with access", "body": "Give someone their own login for this horse. Co-owners can add records; viewers can only look.", "none": "Nobody else has access yet.", "pending": "Invitation sent — waiting for them to accept", "email": "Their email", "emailPlaceholder": "name@example.com", "roleLabel": "Access", "invite": "Invite", "inviting": "Sending…", "remove": "Remove", "cancel": "Cancel", "full": "A horse can be shared with up to {n} people.", "note": "You stay the owner: only you can move this horse between yards, change what's public, or delete it.", "role": {"coOwner": "Co-owner", "viewer": "Viewer"}, "badgeCoOwner": "Shared by {owner}", "badgeViewer": "Shared by {owner} · view only"},
    "share": {"accepted": "You now have access to {horse}", "acceptedBody": "It's in your horses list from now on.", "view": "Open the horse", "failed": "That invitation didn't work", "failedBody": "It may have been withdrawn, already used, or sent to a different email address.", "backHome": "Back to my horses"},
    back: 'My Horses', public: '🌍 Public', private: '🔒 Private', viewPublic: 'View public page', followers: 'followers',
    notFound: 'Horse not found', notFoundBack: 'Back to my horses',
    sections: { info: 'Information', photos: 'Photos', news: 'News', medical: 'Medical', reminders: 'Reminders', documents: 'Documents', sharing: 'Sharing', pedigree: 'Pedigree' },
    about: 'About', noAbout: 'No description yet.', details: 'Details', edit: 'Edit', editHorse: 'Edit horse', deleteHorse: 'Delete this horse', deleteConfirm: 'Delete this horse and all its records? This cannot be undone.',
    fields: {
      name: 'Name', dob: 'Date of birth', breed: 'Breed', gender: 'Gender', colour: 'Colour', height: 'Height (hh)', discipline: 'Discipline',
      status: 'Status', microchip: 'Microchip', passport: 'Passport', country: 'Country', city: 'City', location: 'Location',
      category: 'Category', title: 'Title', date: 'Date', vet: 'Vet / practitioner', notes: 'Notes', interval: 'Repeat every', nextDue: 'Next due',
    },
    gallery: 'Photo gallery', upload: '+ Upload', setProfilePhoto: 'Set main photo', noPhotos: 'No photos yet. Add some to bring the profile to life.',
    newsTitle: 'News', addNews: '+ Post news', noNews: 'No news yet. Share an update about your horse.', newsTitleField: 'Headline', newsBody: 'What’s the news?', addImages: 'Add images', publish: 'Publish',
    medicalTitle: 'Medical book — private', addRecord: '+ Add record', noMedical: 'No records yet. Log a vaccination, dental or vet visit.',
    remindersTitle: 'Care reminders — private', addReminder: '+ Add reminder', attachDocs: 'Attach documents', attachHint: 'Photos or PDFs — vet reports, x-rays, lab results.', attachment: 'Attachment', remindersHint: 'You’ll get an email when each one is due.', noReminders: 'No reminders yet.', next: 'Next:', everyMonths: 'every {n} months', markDone: 'Mark done',
    documentsTitle: 'Documents — private', noDocuments: 'No documents yet. Upload a passport, registration or insurance.', download: 'Open',
    sharingTitle: 'Sharing & privacy', makePublic: 'Public profile', makePublicHint: 'Let anyone view this horse via a link.', whatPublic: 'What’s visible when public', alwaysPrivate: 'Medical, reminders and documents are always private — never shown publicly.',
    vis: { public: 'Public', private: 'Private', country: 'Country only', city: 'Country + city' },
  },
  public: {
    badge: '🌍 Public profile', share: '🔗 Share link', linkCopied: 'Link copied!',
    follow: '+ Follow', following: '✓ Following', signInToFollow: 'Sign in to follow',
    photos: 'Photos', latestNews: 'Latest news', privateNote: 'Medical records, documents and reminders are private and never shown on public profiles.',
    privateTitle: 'This profile is private', privateBody: 'This horse isn’t publicly shared, or the link is wrong.', backDiscover: 'Discover horses',
  },
  discover: {
    title: 'Discover horses', subtitle: 'Browse public horses from owners around the world.',
    tabDiscover: 'Discover', tabFollowing: 'Following',
    filterCountry: 'Country', filterBreed: 'Breed', filterDiscipline: 'Discipline', anyGender: 'Any gender',
    filterHint: 'Tip: filters match the start of a word (e.g. “Irish”, not “sport”).',
    emptyTitle: 'No horses found', emptyBody: 'Try removing a filter, or check back soon.',
    followingEmptyTitle: 'You’re not following anyone yet', followingEmptyBody: 'Follow horses you love to see their latest news here.',
  },
  billing: {
    paid: 'Payment received — thank you! Your subscription activates in a moment.', cancelled: 'Checkout cancelled — nothing was charged.', redirecting: 'Taking you to secure checkout…',
    activeTitle: 'You’re all set', activeBody: 'Your subscription is active. Thank you!',
    lockedTitle: 'Your free trial has ended', lockedBody: 'Add a payment method to keep managing your horses.',
    trialTitle: 'Free trial', trialBody: '{days} days left — your trial ends {date}.',
    month: 'month', feat1: 'Up to 3 horses', feat2: 'Medical book, reminders & documents', feat3: 'Public profiles & following',
    subscribe: 'Add payment method', mockNote: 'Demo only — no real payment is taken.', backToApp: 'Back to my horses', daysLeft: '{days} days left',
  },
  settings: {
    title: 'Settings', profile: 'Your profile', changePhoto: 'Change photo', saved: 'Saved',
    plan: 'Plan', currentPlan: 'Current plan', trialEnds: 'Trial ends {date}', manageBilling: 'Manage billing',
    storage: 'Storage', signOut: 'Sign out',
  },
  legal: { updated: 'Last updated: {date}' },
  admin: {"title": "Support console", "subtitle": "Read-only view of yards and accounts, for helping people who get stuck.", "tab": {"yards": "Yards", "users": "Accounts"}, "stat": {"users": "Accounts", "yards": "Yards", "horses": "Horses", "boardings": "Boarded", "shares": "Shared horses", "invites": "Pending invites"}, "members": "{n} people", "horses": "{n} horses", "owner": "Owner", "people": "People on the yard", "boardedHorses": "Boarded horses", "invites": "Invitations", "theirHorses": "Their horses", "ownedYards": "Yards they own", "memberOf": "Member of", "created": "Created {date}", "none": "None", "onboarded": "Set up", "notOnboarded": "Not finished setting up", "privacyNote": "Medical records, documents and photos are never shown here — they stay between an owner and their yard."},
  notFound: { title: 'Page not found', back: 'Back home' },
}

const da: Dict = {
  "nav": {
    "features": "Funktioner",
    "how": "Sådan virker det",
    "discover": "Opdag",
    "myHorses": "Mine heste",
    "myFarm": "Min stald",
    "signIn": "Log ind",
    "getStarted": "Kom i gang",
    "settings": "Indstillinger",
    "billing": "Betaling",
    "signOut": "Log ud",
    "spaceSwitch": "Skift mellem dine heste og din stald",
    "menu": "Menu",
    "account": "Konto",
    "tasks": "Opgaver",
    "yard": "Stald",
    "home": "Hjem"
  },
  "features": {
    "title": "Alt hvad HorseSheli kan",
    "subtitle": "Én konto, to apps — til ejerne, der fører journalen, og staldene, der har hestene i pension.",
    "cta": "Kom gratis i gang",
    "sections": [
      {
        "icon": "🔑",
        "title": "Konto & adgang",
        "items": [
          "Opret dig & log ind",
          "Bekræft e-mail",
          "Glemt / nulstil adgangskode",
          "Vælg din rolle ved oprettelse",
          "Vær hesteejer, stald eller begge",
          "Rolletilpasset navigation",
          "Beskyttelse mod bots & engangsmails",
          "90 dages gratis prøveperiode"
        ]
      },
      {
        "icon": "👤",
        "title": "Ejerprofil",
        "items": [
          "Navn & profilbillede",
          "Om dig, land & by",
          "Et “første hesteminde”",
          "Lagermåler",
          "Lyst / mørkt tema",
          "Engelsk, dansk & tysk"
        ]
      },
      {
        "icon": "🐎",
        "title": "Hestens profil",
        "items": [
          "Opret, rediger & slet heste",
          "Race, køn, alder, højde, farve, disciplin",
          "Chip & pas (altid privat)",
          "Profilbillede",
          "Op til 3 heste gratis"
        ]
      },
      {
        "icon": "🩺",
        "title": "Sygejournal",
        "items": [
          "En privat tidslinje over pleje",
          "11 typer journaler",
          "Dyrlæge, dato & noter",
          "Vedhæft dyrlægerapporter & røntgen",
          "Kun din, medmindre du deler den"
        ]
      },
      {
        "icon": "🔔",
        "title": "Påmindelser",
        "items": [
          "Tilbagevendende beslag, vaccination, ormekur m.m.",
          "Hver 1.–4. måned",
          "Markér som udført — den ruller frem",
          "E-mail når pleje forfalder"
        ]
      },
      {
        "icon": "📄",
        "title": "Dokumenter",
        "items": [
          "Upload PDF’er, billeder & filer",
          "Download når som helst",
          "5 gratis pr. hest, 50 på premium"
        ]
      },
      {
        "icon": "📷",
        "title": "Billeder & nyheder",
        "items": [
          "Fotogalleri (Premium)",
          "Vælg et profilbillede",
          "Nyheder med billeder (Premium)",
          "Alle billeder tjekkes automatisk"
        ]
      },
      {
        "icon": "🌐",
        "title": "Socialt & opdagelse",
        "items": [
          "Gør en hest offentlig (Premium)",
          "Bestem hvad der er synligt",
          "Land, by eller helt privat",
          "Opdag & filtrer offentlige heste",
          "Følg heste & få et feed (Premium)"
        ]
      },
      {
        "icon": "🏡",
        "title": "Til stalde",
        "items": [
          "Opret din stald",
          "Adresse, postnummer & nødkontakt",
          "Ét overblik over alle heste",
          "Pensionærliste med kontakter",
          "Chip & pas på ét blik",
          "Påmindelser for hele stalden"
        ]
      },
      {
        "icon": "✉️",
        "title": "Invitationer",
        "items": [
          "Inviter pensionærer via e-mail eller link",
          "Se afventende & accepterede",
          "Tilbagekald når som helst",
          "Sikkert — bundet til den inviterede e-mail"
        ]
      },
      {
        "icon": "📍",
        "title": "Opstaldning",
        "items": [
          "Tilslut dig stalde via invitation",
          "Placer hver hest i en stald",
          "Flyt frit mellem stalde",
          "Opstald i en stald, du selv ejer",
          "Skjul hvad du vil for stalden"
        ]
      },
      {
        "icon": "🎫",
        "title": "Support-sager",
        "items": [
          "Skriv til din stald",
          "Vælg kategori & prioritet",
          "Vedhæft en hest",
          "Stalde får en indbakke"
        ]
      },
      {
        "icon": "💳",
        "title": "Abonnementer",
        "items": [
          "90 dages gratis prøveperiode",
          "Premium for hesteejere",
          "Stalde — £1.99 om måneden",
          "Kortbetaling (klar til Stripe)"
        ]
      },
      {
        "icon": "🛡️",
        "title": "Sikkerhed & privatliv",
        "items": [
          "Billedmoderering ved hver upload",
          "Dine sygejournaler forbliver private",
          "Sikker login & kryptering",
          "Hostet i EU (Dublin)"
        ]
      }
    ]
  },
  "farm": {
    "onboard": {
      "badge": "🏡 Til stalde",
      "title": "Opret din stald",
      "subtitle": "Dine ejere fører deres hestes journaler — du får ét klart overblik over hele stalden.",
      "name": "Staldens navn",
      "namePlaceholder": "f.eks. Willowbrook Stald",
      "about": "Om stalden",
      "country": "Land",
      "city": "By",
      "cta": "Start 3 måneders gratis prøveperiode"
    },
    "dash": {
      "title": "Stald-dashboard",
      "trialBanner": "Prøveperiode — {days} dage tilbage",
      "trialCta": "Administrér abonnement",
      "lockedBanner": "Din prøveperiode er slut — abonnér for at administrere stalden.",
      "roster": "Heste i stalden",
      "rosterEmpty": "Ingen heste i stalden endnu",
      "rosterEmptyBody": "Inviter dine ejere — når de accepterer og opstalder en hest, vises den her.",
      "invite": "Inviter en ejer",
      "yardReminders": "Kommer snart i stalden",
      "noReminders": "Intet forfalder snart ✅",
      "members": "{n} ejere",
      "horsesCount": "{n} heste",
      "dueSoon": "{n} forfalder snart",
      "owner": "Ejer",
      "ownersLabel": "Ejere",
      "compliance": "Kommer snart",
      "allGood": "Opdateret",
      "view": "Se"
    },
    "invites": {
      "title": "Inviter ejere",
      "email": "Ejerens e-mail",
      "emailPlaceholder": "ejer@email.com",
      "name": "Deres navn (valgfrit)",
      "send": "Send invitation",
      "sending": "Sender…",
      "copyLink": "Kopiér link",
      "copied": "Kopieret!",
      "pending": "Afventer",
      "accepted": "Tilmeldt",
      "revoke": "Tilbagekald",
      "none": "Ingen invitationer endnu.",
      "note": "De får en e-mail med et link — eller kopiér det og send det selv.",
      "role": "De er…"
    },
    "horse": {
      "back": "← Tilbage til stalden",
      "owner": "Ejer",
      "medical": "Sygejournal",
      "documents": "Dokumenter",
      "reminders": "Påmindelser",
      "hidden": "Skjult af ejer",
      "noMedical": "Ingen sygejournaler delt.",
      "noDocuments": "Ingen dokumenter delt.",
      "noReminders": "Ingen påmindelser delt.",
      "nextDue": "Næste",
      "download": "Download",
      "location": "Placering på stalden"
    },
    "billing": {
      "title": "Stald-abonnement",
      "trialBody": "Du er på gratis prøveperiode — {days} dage tilbage (indtil {date}).",
      "lockedBody": "Din prøveperiode er slut. Abonnér for fortsat at administrere din stald.",
      "perMonth": "/måned",
      "price": "£1.99 / måned",
      "feat1": "Alle heste i stalden på ét blik",
      "feat2": "Overblik, sundhed & påmindelser",
      "feat3": "Inviter ubegrænset antal ejere",
      "subscribe": "Tilføj betaling & abonnér",
      "activeTitle": "Abonnement aktivt",
      "activeBody": "Tak — din stald er fuldt aktiv.",
      "back": "Tilbage til dashboard",
      "mockNote": "Demo-betaling — der trækkes intet kort i denne forhåndsvisning."
    },
    "uk": {
      "editYard": "Staldens oplysninger",
      "addressLine": "Adresse",
      "postcode": "Postnummer",
      "phone": "Staldens telefon",
      "emergencyName": "Nødkontakt",
      "emergencyPhone": "Nødtelefon",
      "contactCard": "Staldens kontakt",
      "emergency": "Nødstilfælde",
      "microchip": "Chip",
      "passport": "Pas",
      "save": "Gem oplysninger",
      "saved": "Oplysninger gemt"
    },
    "my": {
      "title": "Opstaldning",
      "subtitle": "Stalde, der har inviteret dig — og hvor hver af dine heste står.",
      "joinedHeading": "Stalde, du er med i",
      "joinedHint": "Din egen stald vises her automatisk. Andre vises, når de inviterer dig, og du accepterer.",
      "horsesHeading": "Hvor dine heste står",
      "placeHint": "Placér hver hest på en af dine stalde.",
      "stabledAt": "Opstaldet på",
      "notStabled": "Ikke på en stald",
      "visibleToYard": "Delt med stalden",
      "none": "Du er ikke i en stald endnu",
      "noneBody": "Når en stald inviterer dig, kan du acceptere her og placere dine heste.",
      "member": "Du er medlem af {farm}.",
      "board": "Opstald her",
      "boarded": "Opstaldet her ✓",
      "unboard": "Fjern",
      "shareTitle": "Hvad {farm} kan se",
      "shareBody": "Din stald ser disse som standard. Fjern markeringen for det, du vil holde privat.",
      "catMedical": "Sygejournal",
      "catDocuments": "Dokumenter",
      "catReminders": "Påmindelser",
      "catInfo": "Detaljer (race, alder…)",
      "yourYard": "Din stald",
      "stableLocation": "Hvor på stalden",
      "stableLocationPlaceholder": "f.eks. boks 4, vestfold",
      "peopleHeading": "Hvem er på stalden",
      "peopleHint": "Andre, der opstalder eller arbejder her."
    },
    "accept": {
      "title": "Tilmeld dig {farm} på HorseSheli",
      "heading": "Tilmeld dig en stald på HorseSheli",
      "invitedYou": "{farm} har inviteret dig",
      "body": "Accepter for at blive tilknyttet stalden. Du beholder fuld kontrol over dine heste og kan skjule hvad du vil.",
      "cta": "Accepter invitation",
      "busy": "Tilmelder…",
      "move": "Flyt hertil",
      "moveNote": "Du er allerede i en stald — accept flytter dig (og dine heste) hertil.",
      "signin": "Log ind eller opret en gratis konto for at acceptere denne invitation.",
      "doneTitle": "Du er nu tilmeldt {farm} 🎉",
      "doneBody": "Opstald en hest for at dele den med stalden.",
      "invalid": "Invitation ikke fundet",
      "invalidBody": "Linket kan være tilbagekaldt eller allerede brugt.",
      "goApp": "Gå til mine heste"
    },
    "switchHorses": "Mine heste",
    "switchFarm": "Min stald",
    "roles": {
      "owner": "Staldejer",
      "manager": "Leder",
      "groom": "Staldhjælp",
      "boarder": "Pensionær (hesteejer)"
    },
    "packages": {
      "full": "Fuld pension",
      "part": "Delpension",
      "diy": "Selvpasser",
      "grass": "Græspension",
      "schooling": "Træningspension",
      "retirement": "Aftægtspension",
      "other": "Andet",
      "label": "Pensionstype",
      "none": "Ingen type valgt",
      "prices": "Priser på pensionstyper",
      "pricesHint": "Månedspris for hver type, du tilbyder — bruges til at lave fakturaer automatisk. Lad felter stå tomme for typer, du ikke tilbyder.",
      "perMonthShort": "/md."
    },
    "tasks": {
      "title": "Daglige opgaver",
      "today": "I dag",
      "add": "+ Tilføj opgave",
      "addTitle": "Tilføj en opgave",
      "taskName": "Hvad skal der gøres?",
      "taskPlaceholder": "f.eks. Tag markheste ind, aftenfodring",
      "notes": "Noter (valgfrit)",
      "forHorse": "Til en hest (valgfrit)",
      "wholeYard": "Hele stalden",
      "assignTo": "Tildel til (valgfrit)",
      "anyone": "Alle",
      "empty": "Ingen opgaver denne dag",
      "emptyBody": "Tilføj dagens opgaver — fodring, folde, dækkener, medicin — så krydser teamet dem af.",
      "copyYesterday": "Kopiér gårsdagens liste",
      "copied": "{n} opgaver kopieret",
      "done": "Færdig",
      "todo": "Skal gøres",
      "doneBy": "Udført af {name}",
      "progress": "{done} af {total} udført",
      "allDone": "Alt klaret for i dag 🎉",
      "deleteTask": "Slet opgave"
    },
    "members": {
      "title": "Personer i stalden",
      "staff": "Personale",
      "liveries": "Pensionærer",
      "horsesHere": "{n} hest(e) her",
      "joined": "Tilmeldt {date}",
      "role": "Rolle",
      "changeRole": "Skift rolle",
      "remove": "Fjern fra stalden",
      "removeConfirm": "Fjern {name} fra stalden? Deres heste forlader også stalden.",
      "empty": "Ingen er tilmeldt endnu",
      "emptyBody": "Inviter dine pensionærer og personale — de vises her, når de accepterer."
    },
    "invoices": {
      "title": "Fakturaer",
      "myTitle": "Mine fakturaer",
      "raise": "+ Ny faktura",
      "raiseTitle": "Ny faktura",
      "billTo": "Faktureres til",
      "autoFill": "Byg ud fra pensionstyper",
      "autoFillHint": "Tilføjer en linje for hver af deres heste ud fra dine priser.",
      "period": "Periode (f.eks. august 2026)",
      "dueDate": "Forfaldsdato",
      "notes": "Noter (valgfrit)",
      "line": "Linje",
      "lineDesc": "Beskrivelse",
      "amount": "Beløb (£)",
      "addLine": "+ Tilføj linje",
      "total": "I alt",
      "create": "Opret kladde",
      "creating": "Opretter…",
      "send": "Send til {name}",
      "sendShort": "Send",
      "markPaid": "Markér betalt",
      "voidIt": "Annuller",
      "edit": "Rediger",
      "empty": "Ingen fakturaer endnu",
      "emptyBody": "Angiv dine pensionspriser, og lav så din første faktura med to tryk.",
      "emptyMine": "Ingen fakturaer endnu — når din stald fakturerer dig, vises det her.",
      "st": {
        "draft": "Kladde",
        "sent": "Sendt",
        "paid": "Betalt",
        "void": "Annulleret"
      },
      "payNote": "Betal via bankoverførsel, eller som din stald foretrækker — de markerer den som betalt.",
      "sentOn": "Sendt {date}",
      "paidOn": "Betalt {date}",
      "due": "Forfalder {date}",
      "noPrices": "Angiv først dine pensionspriser (Staldens oplysninger), eller tilføj linjer manuelt."
    }
  },
  "tickets": {
    "title": "Sager",
    "dashTitle": "Sager fra opstaldere",
    "open": "Åbne",
    "resolved": "Løste",
    "all": "Alle",
    "openCount": "{n} åbne",
    "raise": "Opret en sag",
    "noneOwner": "Ingen sager endnu — når en opstalder opretter en, vises den her.",
    "noneMine": "Du har ikke oprettet nogen sager endnu.",
    "viewAll": "Se alle sager",
    "subject": "Emne",
    "subjectPlaceholder": "f.eks. Ekstra hø til Comet",
    "message": "Detaljer",
    "messagePlaceholder": "Hvad har du brug for?",
    "yard": "Stald",
    "category": "Kategori",
    "priority": "Prioritet",
    "horse": "Om en hest",
    "horseNone": "Generelt (ikke hesterelateret)",
    "submit": "Send til stalden",
    "sending": "Sender…",
    "markResolved": "Markér som løst",
    "reopen": "Genåbn",
    "by": "Fra",
    "about": "om",
    "cat": {
      "general": "Generelt",
      "care": "Pleje & foder",
      "facilities": "Faciliteter",
      "health": "Sundhed",
      "billing": "Betaling"
    },
    "prio": {
      "normal": "Normal",
      "urgent": "Haster"
    },
    "st": {
      "open": "Åben",
      "resolved": "Løst"
    }
  },
  "footer": {
    "tagline": "En roligere måde at holde styr på alle heste — og hele stalden.",
    "privacy": "Privatliv",
    "terms": "Vilkår"
  },
  "crash": {
    "title": "Noget gik galt",
    "body": "Siden stødte på et problem. Prøv igen, eller genindlæs — dine data er sikre.",
    "retry": "Prøv igen",
    "reload": "Genindlæs"
  },
  "update": {
    "available": "En ny version er tilgængelig",
    "reload": "Genindlæs",
    "later": "Afvis"
  },
  "units": {
    "yrs": "år",
    "hh": "hh"
  },
  "common": {
    "save": "Gem",
    "delete": "Slet",
    "cancel": "Annullér",
    "close": "Luk"
  },
  "confirm": {
    "deleteTitle": "Slet dette?",
    "deleteBody": "Dette kan ikke fortrydes."
  },
  "errors": {
    "rateLimited": "For mange forsøg — vent et øjeblik og prøv igen.",
    "inviteEmailMismatch": "Denne invitation blev sendt til en anden e-mailadresse. Log ind med den inviterede e-mail for at acceptere.",
    "invalidInput": "Noget så ikke rigtigt ud — tjek formularen og prøv igen.",
    "generic": "Noget gik galt. Prøv igen.",
    "trialExpired": "Din prøveperiode er slut — abonnér for at fortsætte.",
    "imageRejected": "Billedet blev blokeret af vores indholdstjek.",
    "quota": "Du har nået din lagergrænse.",
    "docLimit": "Du har nået dokumentgrænsen for denne hest.",
    "horseLimit": "Du har nået hestegrænsen på din plan.",
    "notMember": "Du skal være medlem af denne stald først.",
    "unauthorized": "Log venligst ind igen.",
    "required": "Udfyld venligst: {fields}"
  },
  "gender": {
    "Mare": "Hoppe",
    "Stallion": "Hingst",
    "Gelding": "Vallak"
  },
  "status": {
    "active": "aktiv",
    "sold": "solgt",
    "retired": "pensioneret",
    "deceased": "død"
  },
  "colour": {
    "Bay": "Brun",
    "Dark Bay": "Mørkbrun",
    "Chestnut": "Fuks",
    "Grey": "Skimmel",
    "Dun": "Blakket"
  },
  "discipline": {
    "Eventing": "Military",
    "Dressage": "Dressur",
    "Showing": "Showing"
  },
  "medicalCategory": {
    "vaccination": "Vaccination",
    "deworming": "Ormekur",
    "dental": "Tandpleje",
    "farrier": "Beslag",
    "vet_exam": "Dyrlægetjek",
    "bloodwork": "Blodprøve",
    "injury": "Skade",
    "medication": "Medicin",
    "allergy": "Allergi",
    "weight_condition": "Vægt & huld",
    "other": "Andet"
  },
  "landing": {
    "heroBadge": "🐎 Til hesteejere, af hestefolk",
    "heroTitleA": "Hele din hests liv,",
    "heroTitleB": "smukt organiseret.",
    "heroSubtitle": "Journaler, dokumenter, sygehistorik og påmindelser for hver hest, du ejer — privat som standard, delbart når du vil. Ingen regneark. Ingen papirkasser.",
    "getStarted": "Start gratis prøveperiode →",
    "seePublic": "Opdag heste",
    "twoDoor": {
      "title": "To veje ind",
      "horseTitle": "Jeg er hesteejer",
      "horseBody": "Saml din hests profil, billeder, sygejournal og påmindelser ét roligt sted. Gratis at starte.",
      "horseCta": "Start gratis →",
      "farmTitle": "Jeg driver en stald",
      "farmBody": "Se alle heste i stalden på ét blik — overblik, sundhed, påmindelser — og inviter dine ejere. 3 måneders gratis prøveperiode.",
      "farmCta": "Start din stald →"
    },
    "trialNote": "3 måneder gratis — intet kort kræves for at starte.",
    "features": {
      "title": "Alt en hest har brug for — uden papirarbejdet",
      "subtitle": "HorseSheli er først og fremmest et styringsværktøj. Roligt, klart og bygget, så en ikke-teknisk ejer kan bruge det uden vejledning.",
      "items": [
        {
          "title": "Alle oplysninger ét sted",
          "body": "Info, billeder, dokumenter, sygehistorik, nyheder — hver hest i klare sektioner, du når med ét tryk."
        },
        {
          "title": "Glem aldrig en dato igen",
          "body": "Smed, ormekur, vaccinationer, tandpleje — venlige e-mailpåmindelser, før tingene forfalder."
        },
        {
          "title": "Du bestemmer, hvad der deles",
          "body": "Profiler er private som standard. Gør en hest — eller blot dens billeder — offentlig, når du vil, sektion for sektion."
        },
        {
          "title": "Dokumenter, der aldrig forsvinder",
          "body": "Pas, registrering, forsikring, certifikater — uploadet én gang, sikkert for altid."
        },
        {
          "title": "En sygehistorik, der følger med",
          "body": "Vaccinationer, behandlinger, dyrlægebesøg i én tidslinje. Del den med en dyrlæge på sekunder."
        },
        {
          "title": "Forbind, uden at scrolle",
          "body": "Følg heste du holder af, del et profillink, se deres nyheder. Et netværk — uden støj fra et feed."
        }
      ]
    },
    "how": {
      "title": "Klar på få minutter",
      "steps": [
        {
          "title": "Tilføj din hest",
          "body": "Navn, billede, race, det basale. Tager et minut."
        },
        {
          "title": "Udfyld livet",
          "body": "Upload dokumenter, registrér sundhed og pleje. Eller gør det lidt ad gangen."
        },
        {
          "title": "Del hvis du vil",
          "body": "Hold det privat, eller udgiv en smuk profil at dele."
        }
      ]
    },
    "privacy": {
      "badge": "🔒 Privatliv er standard",
      "title": "Privat, indtil du siger andet",
      "body": "Sygejournaler, dokumenter og påmindelser er private for dig, punktum. Billeder, basisinfo og nyheder kan gøres offentlige, sektion for sektion — så du deler præcis det, du vil.",
      "bullets": [
        "Hver hest: offentlig eller privat",
        "Hver sektion: styres uafhængigt",
        "Sundhed & dokumenter: altid private",
        "Del et rent offentligt link med ét tryk"
      ]
    },
    "cta": {
      "title": "Kig dig omkring",
      "body": "Start din gratis 3-måneders prøveperiode og fortæl os, hvad der ville gøre den virkelig nyttig for din stald.",
      "button": "Start gratis prøveperiode →"
    }
  },
  "auth": {
    "magic": {"or": "eller", "cta": "Send mig et login-link", "sent": "Tjek din e-mail — vi har sendt et link, der logger dig direkte ind. Det holder i 15 minutter.", "needEmail": "Indtast din e-mailadresse først.", "failedTitle": "Linket virkede ikke", "expired": "Login-links holder 15 minutter og virker én gang. Bed om et nyt, så er du inde.", "badLink": "Linket ser ufuldstændigt ud. Bed om et nyt nedenfor.", "tryAgain": "Send mig et nyt link"},
    "email": "E-mail",
    "password": "Adgangskode",
    "newPassword": "Ny adgangskode",
    "agreePre": "Ved at oprette en konto accepterer du",
    "agreeTerms": "vilkårene",
    "agreeAnd": "og",
    "agreePrivacy": "privatlivspolitikken",
    "agreePost": ".",
    "chooseRole": "Først det vigtigste — hvordan vil du bruge HorseSheli?",
    "subtitleHorse": "Gratis til dine hestes journaler, fotos og påmindelser.",
    "subtitleFarm": "Start staldens 3 måneders gratis prøveperiode — uden kort.",
    "roleHorseDesc": "Mine hestes journaler, sundhedsbog og påmindelser — og stalden, de bor på.",
    "roleFarmDesc": "Driv stalden: pensionærer og personale, daglige opgaver og fakturering.",
    "code": "Bekræftelseskode",
    "passwordHint": "Mindst 10 tegn, med store og små bogstaver og et tal.",
    "noAccount": "Ingen konto endnu?",
    "haveAccount": "Har du allerede en konto?",
    "forgotLink": "Glemt adgangskode?",
    "resend": "Send kode igen",
    "backToSignin": "Tilbage til log ind",
    "backHome": "Tilbage til forsiden",
    "signin": {
      "title": "Velkommen tilbage",
      "subtitle": "Log ind på din HorseSheli-konto.",
      "cta": "Log ind"
    },
    "signup": {
      "title": "Opret din konto",
      "subtitle": "Start din gratis 3-måneders prøveperiode.",
      "cta": "Tilmeld"
    },
    "confirm": {
      "title": "Tjek din e-mail",
      "subtitle": "Indtast koden, vi lige har sendt.",
      "cta": "Bekræft"
    },
    "forgot": {
      "title": "Nulstil din adgangskode",
      "subtitle": "Vi sender dig en nulstillingskode.",
      "cta": "Send kode"
    },
    "reset": {
      "title": "Vælg ny adgangskode",
      "subtitle": "Indtast koden og din nye adgangskode.",
      "cta": "Nulstil adgangskode"
    }
  },
  "onboarding": {
    "badge": "Velkommen",
    "title": "Lad os oprette din profil",
    "subtitle": "Et par detaljer, så andre ejere ved, hvem du er. Du kan ændre dem når som helst.",
    "addPhoto": "Tilføj et billede",
    "name": "Dit navn",
    "about": "Om dig",
    "aboutPlaceholder": "Rytter hele livet, amatør-dressur…",
    "country": "Land",
    "city": "By",
    "memory": "Et yndlingsminde med heste",
    "memoryPlaceholder": "Den første pony du red på…",
    "cta": "Fortsæt til mine heste →"
  },
  "dashboard": {
    "trialBanner": "Du er på gratis prøveperiode — {days} dage tilbage.",
    "trialCta": "Administrér abonnement",
    "welcome": "Velkommen tilbage, {name}",
    "addHorse": "+ Tilføj hest",
    "stats": {
      "horses": "Heste",
      "public": "Offentlige profiler",
      "storage": "Lagerplads brugt"
    },
    "myHorses": "Mine heste",
    "comingSoon": "Flere heste — kommer snart",
    "empty": {
      "title": "Ingen heste endnu",
      "body": "Tilføj din første hest for at begynde at opbygge dens profil, sygebog og mere."
    }
  },
  "horse": {
    "access": {"title": "Personer med adgang", "body": "Giv en anden deres eget login til denne hest. Medejere kan tilføje journaler; seere kan kun kigge.", "none": "Ingen andre har adgang endnu.", "pending": "Invitation sendt — venter på accept", "email": "Deres e-mail", "emailPlaceholder": "navn@eksempel.dk", "roleLabel": "Adgang", "invite": "Invitér", "inviting": "Sender…", "remove": "Fjern", "cancel": "Annullér", "full": "En hest kan deles med op til {n} personer.", "note": "Du forbliver ejer: kun du kan flytte hesten mellem stalde, ændre hvad der er offentligt, eller slette den.", "role": {"coOwner": "Medejer", "viewer": "Seer"}, "badgeCoOwner": "Delt af {owner}", "badgeViewer": "Delt af {owner} · kun visning"},
    "share": {"accepted": "Du har nu adgang til {horse}", "acceptedBody": "Den er på din hesteliste fra nu af.", "view": "Åbn hesten", "failed": "Invitationen virkede ikke", "failedBody": "Den kan være trukket tilbage, allerede brugt eller sendt til en anden e-mailadresse.", "backHome": "Tilbage til mine heste"},
    "back": "Mine heste",
    "public": "🌍 Offentlig",
    "private": "🔒 Privat",
    "viewPublic": "Se offentlig side",
    "followers": "følgere",
    "notFound": "Hest ikke fundet",
    "notFoundBack": "Tilbage til mine heste",
    "sections": {
      "info": "Information",
      "photos": "Billeder",
      "news": "Nyheder",
      "medical": "Sundhed",
      "reminders": "Påmindelser",
      "documents": "Dokumenter",
      "sharing": "Deling",
      "pedigree": "Stamtavle"
    },
    "about": "Om",
    "noAbout": "Ingen beskrivelse endnu.",
    "details": "Detaljer",
    "edit": "Rediger",
    "editHorse": "Rediger hest",
    "deleteHorse": "Slet denne hest",
    "deleteConfirm": "Slet denne hest og alle dens journaler? Dette kan ikke fortrydes.",
    "fields": {
      "name": "Navn",
      "dob": "Fødselsdato",
      "breed": "Race",
      "gender": "Køn",
      "colour": "Farve",
      "height": "Højde (hh)",
      "discipline": "Disciplin",
      "status": "Status",
      "microchip": "Microchip",
      "passport": "Pas",
      "country": "Land",
      "city": "By",
      "location": "Sted",
      "category": "Kategori",
      "title": "Titel",
      "date": "Dato",
      "vet": "Dyrlæge / behandler",
      "notes": "Noter",
      "interval": "Gentag hver",
      "nextDue": "Næste gang"
    },
    "gallery": "Billedgalleri",
    "upload": "+ Upload",
    "setProfilePhoto": "Vælg hovedbillede",
    "noPhotos": "Ingen billeder endnu. Tilføj nogle for at give profilen liv.",
    "newsTitle": "Nyheder",
    "addNews": "+ Skriv nyhed",
    "noNews": "Ingen nyheder endnu. Del en opdatering om din hest.",
    "newsTitleField": "Overskrift",
    "newsBody": "Hvad er nyt?",
    "addImages": "Tilføj billeder",
    "publish": "Udgiv",
    "medicalTitle": "Sygebog — privat",
    "addRecord": "+ Tilføj journal",
    "noMedical": "Ingen journaler endnu. Registrér en vaccination, tandpleje eller dyrlægebesøg.",
    "remindersTitle": "Plejepåmindelser — privat",
    "addReminder": "+ Tilføj påmindelse",
    "attachDocs": "Vedhæft dokumenter",
    "attachHint": "Billeder eller PDF’er — dyrlægerapporter, røntgen, prøvesvar.",
    "attachment": "Vedhæftning",
    "remindersHint": "Du får en e-mail, når hver enkelt forfalder.",
    "noReminders": "Ingen påmindelser endnu.",
    "next": "Næste:",
    "everyMonths": "hver {n}. måned",
    "markDone": "Markér udført",
    "documentsTitle": "Dokumenter — private",
    "noDocuments": "Ingen dokumenter endnu. Upload et pas, registrering eller forsikring.",
    "download": "Åbn",
    "sharingTitle": "Deling & privatliv",
    "makePublic": "Offentlig profil",
    "makePublicHint": "Lad alle se denne hest via et link.",
    "whatPublic": "Hvad er synligt, når offentlig",
    "alwaysPrivate": "Sundhed, påmindelser og dokumenter er altid private — vises aldrig offentligt.",
    "vis": {
      "public": "Offentlig",
      "private": "Privat",
      "country": "Kun land",
      "city": "Land + by"
    }
  },
  "public": {
    "badge": "🌍 Offentlig profil",
    "share": "🔗 Del link",
    "linkCopied": "Link kopieret!",
    "follow": "+ Følg",
    "following": "✓ Følger",
    "signInToFollow": "Log ind for at følge",
    "photos": "Billeder",
    "latestNews": "Seneste nyheder",
    "privateNote": "Sygejournaler, dokumenter og påmindelser er private og vises aldrig på offentlige profiler.",
    "privateTitle": "Denne profil er privat",
    "privateBody": "Denne hest deles ikke offentligt, eller linket er forkert.",
    "backDiscover": "Opdag heste"
  },
  "discover": {
    "title": "Opdag heste",
    "subtitle": "Gennemse offentlige heste fra ejere verden over.",
    "tabDiscover": "Opdag",
    "tabFollowing": "Følger",
    "filterCountry": "Land",
    "filterBreed": "Race",
    "filterDiscipline": "Disciplin",
    "anyGender": "Alle køn",
    "filterHint": "Tip: filtre matcher starten af et ord (f.eks. “Irish”, ikke “sport”).",
    "emptyTitle": "Ingen heste fundet",
    "emptyBody": "Prøv at fjerne et filter, eller kig forbi snart.",
    "followingEmptyTitle": "Du følger ikke nogen endnu",
    "followingEmptyBody": "Følg heste du holder af for at se deres seneste nyheder her."
  },
  "billing": {
    "paid": "Betaling modtaget — tak! Dit abonnement aktiveres om et øjeblik.",
    "cancelled": "Betaling annulleret — intet blev trukket.",
    "redirecting": "Du sendes til sikker betaling…",
    "activeTitle": "Alt er klar",
    "activeBody": "Dit abonnement er aktivt. Tak!",
    "lockedTitle": "Din gratis prøveperiode er slut",
    "lockedBody": "Tilføj en betalingsmetode for at fortsætte med at administrere dine heste.",
    "trialTitle": "Gratis prøveperiode",
    "trialBody": "{days} dage tilbage — din prøveperiode slutter {date}.",
    "month": "måned",
    "feat1": "Op til 3 heste",
    "feat2": "Sygebog, påmindelser & dokumenter",
    "feat3": "Offentlige profiler & følgere",
    "subscribe": "Tilføj betalingsmetode",
    "mockNote": "Kun demo — ingen rigtig betaling opkræves.",
    "backToApp": "Tilbage til mine heste",
    "daysLeft": "{days} dage tilbage"
  },
  "settings": {
    "title": "Indstillinger",
    "profile": "Din profil",
    "changePhoto": "Skift billede",
    "saved": "Gemt",
    "plan": "Abonnement",
    "currentPlan": "Nuværende plan",
    "trialEnds": "Prøveperiode slutter {date}",
    "manageBilling": "Administrér betaling",
    "storage": "Lagerplads",
    "signOut": "Log ud"
  },
  "legal": {
    "updated": "Senest opdateret: {date}"
  },
  "admin": {"title": "Supportkonsol", "subtitle": "Skrivebeskyttet overblik over stalde og konti til at hjælpe folk, der er gået i stå.", "tab": {"yards": "Stalde", "users": "Konti"}, "stat": {"users": "Konti", "yards": "Stalde", "horses": "Heste", "boardings": "Opstaldet", "shares": "Delte heste", "invites": "Afventende invitationer"}, "members": "{n} personer", "horses": "{n} heste", "owner": "Ejer", "people": "Personer på stalden", "boardedHorses": "Opstaldede heste", "invites": "Invitationer", "theirHorses": "Deres heste", "ownedYards": "Stalde de ejer", "memberOf": "Medlem af", "created": "Oprettet {date}", "none": "Ingen", "onboarded": "Oprettet", "notOnboarded": "Ikke færdig med opsætning", "privacyNote": "Sundhedsjournaler, dokumenter og fotos vises aldrig her — de forbliver mellem ejeren og deres stald."},
  "notFound": {
    "title": "Side ikke fundet",
    "back": "Tilbage til forsiden"
  },
  "authErrors": {
    "incorrect": "E-mail eller adgangskode passer ikke — prøv igen.",
    "notConfirmed": "Din e-mail er ikke bekræftet endnu — tjek din indbakke for koden.",
    "exists": "Der findes allerede en konto med denne e-mail. Prøv at logge ind i stedet.",
    "policy": "Adgangskoder skal have mindst 10 tegn med et stort bogstav, et lille bogstav og et tal.",
    "codeMismatch": "Koden passer ikke — tjek e-mailen og prøv igen.",
    "expired": "Koden er udløbet — vi kan sende dig en ny.",
    "attempts": "For mange forsøg — vent et par minutter og prøv igen.",
    "notFound": "Vi kan ikke finde en konto med den e-mail.",
    "captcha": "Sikkerhedstjekket blev ikke bestået — prøv igen.",
    "generic": "Det virkede ikke — tjek dine oplysninger og prøv igen."
  }
}

const de: Dict = {
  "nav": {
    "features": "Funktionen",
    "how": "So funktioniert’s",
    "discover": "Entdecken",
    "myHorses": "Meine Pferde",
    "myFarm": "Mein Stall",
    "signIn": "Anmelden",
    "getStarted": "Loslegen",
    "settings": "Einstellungen",
    "billing": "Abrechnung",
    "signOut": "Abmelden",
    "spaceSwitch": "Zwischen deinen Pferden und deinem Stall wechseln",
    "menu": "Menü",
    "account": "Konto",
    "tasks": "Aufgaben",
    "yard": "Stall",
    "home": "Start"
  },
  "features": {
    "title": "Alles, was HorseSheli kann",
    "subtitle": "Ein Konto, zwei Apps — für die Besitzer, die die Daten pflegen, und die Ställe, die die Pferde einstellen.",
    "cta": "Kostenlos starten",
    "sections": [
      {
        "icon": "🔑",
        "title": "Konto & Zugang",
        "items": [
          "Registrieren & anmelden",
          "E-Mail bestätigen",
          "Passwort vergessen / zurücksetzen",
          "Rolle bei der Anmeldung wählen",
          "Pferdebesitzer, Stall oder beides",
          "Rollenbasierte Navigation",
          "Schutz vor Bots & Wegwerf-Mails",
          "90 Tage kostenlos testen"
        ]
      },
      {
        "icon": "👤",
        "title": "Besitzerprofil",
        "items": [
          "Name & Profilbild",
          "Über dich, Land & Ort",
          "Eine „erste Pferdeerinnerung“",
          "Speicheranzeige",
          "Helles / dunkles Design",
          "Englisch, Dänisch & Deutsch"
        ]
      },
      {
        "icon": "🐎",
        "title": "Das Pferdeprofil",
        "items": [
          "Pferde anlegen, bearbeiten & löschen",
          "Rasse, Geschlecht, Alter, Größe, Farbe, Disziplin",
          "Chip & Pass (immer privat)",
          "Profilbild",
          "Bis zu 3 Pferde kostenlos"
        ]
      },
      {
        "icon": "🩺",
        "title": "Krankenakte",
        "items": [
          "Eine private Pflege-Chronik",
          "11 Eintragsarten",
          "Tierarzt, Datum & Notizen",
          "Tierarztberichte & Röntgen anhängen",
          "Nur deine, außer du teilst sie"
        ]
      },
      {
        "icon": "🔔",
        "title": "Erinnerungen",
        "items": [
          "Wiederkehrend: Hufschmied, Impfung, Wurmkur u. a.",
          "Alle 1–4 Monate",
          "Als erledigt markieren — rückt vor",
          "E-Mail, wenn Pflege fällig ist"
        ]
      },
      {
        "icon": "📄",
        "title": "Dokumente",
        "items": [
          "PDFs, Bilder & Dateien hochladen",
          "Jederzeit herunterladen",
          "5 gratis pro Pferd, 50 mit Premium"
        ]
      },
      {
        "icon": "📷",
        "title": "Fotos & News",
        "items": [
          "Fotogalerie (Premium)",
          "Profilbild festlegen",
          "News-Beiträge mit Bildern (Premium)",
          "Jedes Bild wird automatisch geprüft"
        ]
      },
      {
        "icon": "🌐",
        "title": "Sozial & Entdecken",
        "items": [
          "Ein Pferd öffentlich machen (Premium)",
          "Bestimme, was sichtbar ist",
          "Land, Ort oder ganz privat",
          "Öffentliche Pferde entdecken & filtern",
          "Pferden folgen & Feed erhalten (Premium)"
        ]
      },
      {
        "icon": "🏡",
        "title": "Für Ställe",
        "items": [
          "Deinen Stall anlegen",
          "Adresse, PLZ & Notfallkontakt",
          "Ein Überblick über alle Pferde",
          "Einsteller-Liste mit Kontakten",
          "Chip & Pass auf einen Blick",
          "Erinnerungen für den ganzen Stall"
        ]
      },
      {
        "icon": "✉️",
        "title": "Einladungen",
        "items": [
          "Einsteller per E-Mail oder Link einladen",
          "Offene & angenommene sehen",
          "Jederzeit widerrufen",
          "Sicher — an die eingeladene E-Mail gebunden"
        ]
      },
      {
        "icon": "📍",
        "title": "Einstellen",
        "items": [
          "Ställen per Einladung beitreten",
          "Jedes Pferd einem Stall zuordnen",
          "Frei zwischen Ställen wechseln",
          "In deinem eigenen Stall einstellen",
          "Verbirg vor dem Stall, was du willst"
        ]
      },
      {
        "icon": "🎫",
        "title": "Support-Tickets",
        "items": [
          "Deinem Stall schreiben",
          "Kategorie & Priorität wählen",
          "Ein Pferd anhängen",
          "Ställe erhalten einen Posteingang"
        ]
      },
      {
        "icon": "💳",
        "title": "Tarife",
        "items": [
          "90 Tage kostenlos testen",
          "Premium für Pferdebesitzer",
          "Ställe — £1.99 pro Monat",
          "Kartenzahlung (Stripe-bereit)"
        ]
      },
      {
        "icon": "🛡️",
        "title": "Sicherheit & Datenschutz",
        "items": [
          "Bildprüfung bei jedem Upload",
          "Deine Krankenakten bleiben privat",
          "Sichere Anmeldung & Verschlüsselung",
          "Gehostet in der EU (Dublin)"
        ]
      }
    ]
  },
  "farm": {
    "onboard": {
      "badge": "🏡 Für Ställe & Höfe",
      "title": "Richte deinen Stall ein",
      "subtitle": "Deine Einsteller pflegen die Daten ihrer Pferde — du bekommst einen klaren Überblick über den ganzen Hof.",
      "name": "Name des Stalls",
      "namePlaceholder": "z. B. Willowbrook Stall",
      "about": "Über den Stall",
      "country": "Land",
      "city": "Ort",
      "cta": "3 Monate kostenlos testen"
    },
    "dash": {
      "title": "Stall-Dashboard",
      "trialBanner": "Testphase — noch {days} Tage",
      "trialCta": "Abo verwalten",
      "lockedBanner": "Deine Testphase ist abgelaufen — abonniere, um deinen Stall zu verwalten.",
      "roster": "Pferde im Stall",
      "rosterEmpty": "Noch keine Pferde im Stall",
      "rosterEmptyBody": "Lade deine Einsteller ein — sobald sie annehmen und ein Pferd einstellen, erscheint es hier.",
      "invite": "Einsteller einladen",
      "yardReminders": "Demnächst im Stall",
      "noReminders": "Nichts in Kürze fällig ✅",
      "members": "{n} Einsteller",
      "horsesCount": "{n} Pferde",
      "dueSoon": "{n} bald fällig",
      "owner": "Besitzer",
      "ownersLabel": "Einsteller",
      "compliance": "Demnächst",
      "allGood": "Aktuell",
      "view": "Ansehen"
    },
    "invites": {
      "title": "Einsteller einladen",
      "email": "E-Mail des Besitzers",
      "emailPlaceholder": "besitzer@email.com",
      "name": "Name (optional)",
      "send": "Einladung senden",
      "sending": "Senden…",
      "copyLink": "Link kopieren",
      "copied": "Kopiert!",
      "pending": "Ausstehend",
      "accepted": "Beigetreten",
      "revoke": "Zurückziehen",
      "none": "Noch keine Einladungen.",
      "note": "Sie erhalten eine E-Mail mit Link — oder kopiere ihn und sende ihn selbst.",
      "role": "Sie sind…"
    },
    "horse": {
      "back": "← Zurück zum Stall",
      "owner": "Besitzer",
      "medical": "Krankenakte",
      "documents": "Dokumente",
      "reminders": "Erinnerungen",
      "hidden": "Vom Besitzer verborgen",
      "noMedical": "Keine Krankenakten geteilt.",
      "noDocuments": "Keine Dokumente geteilt.",
      "noReminders": "Keine Erinnerungen geteilt.",
      "nextDue": "Nächste",
      "download": "Herunterladen",
      "location": "Standort im Stall"
    },
    "billing": {
      "title": "Stall-Abo",
      "trialBody": "Du bist in der Testphase — noch {days} Tage (bis {date}).",
      "lockedBody": "Deine Testphase ist abgelaufen. Abonniere, um deinen Stall weiter zu verwalten.",
      "perMonth": "/Monat",
      "price": "£1.99 / Monat",
      "feat1": "Alle Pferde im Stall auf einen Blick",
      "feat2": "Überblick, Medizin & Erinnerungen",
      "feat3": "Unbegrenzt Einsteller einladen",
      "subscribe": "Zahlung hinzufügen & abonnieren",
      "activeTitle": "Abo aktiv",
      "activeBody": "Danke — dein Stall ist voll aktiv.",
      "back": "Zurück zum Dashboard",
      "mockNote": "Test-Checkout — in dieser Vorschau wird keine Karte belastet."
    },
    "uk": {
      "editYard": "Stall-Details",
      "addressLine": "Adresse",
      "postcode": "Postleitzahl",
      "phone": "Telefon des Stalls",
      "emergencyName": "Notfallkontakt",
      "emergencyPhone": "Notfallnummer",
      "contactCard": "Stall-Kontakt",
      "emergency": "Notfall",
      "microchip": "Chip",
      "passport": "Pass",
      "save": "Details speichern",
      "saved": "Details gespeichert"
    },
    "my": {
      "title": "Stallplätze",
      "subtitle": "Ställe, die dich eingeladen haben — und wo jedes deiner Pferde steht.",
      "joinedHeading": "Ställe, denen du beigetreten bist",
      "joinedHint": "Dein eigener Stall erscheint hier automatisch. Andere erscheinen, sobald sie dich einladen und du annimmst.",
      "horsesHeading": "Wo deine Pferde stehen",
      "placeHint": "Stelle jedes Pferd in einen deiner Ställe.",
      "stabledAt": "Eingestellt in",
      "notStabled": "In keinem Stall",
      "visibleToYard": "Mit dem Stall geteilt",
      "none": "Du bist noch in keinem Stall",
      "noneBody": "Wenn ein Stall dich einlädt, kannst du hier annehmen und deine Pferde einstellen.",
      "member": "Du bist Mitglied von {farm}.",
      "board": "Hier einstellen",
      "boarded": "Hier eingestellt ✓",
      "unboard": "Entfernen",
      "shareTitle": "Was {farm} sehen kann",
      "shareBody": "Dein Stall sieht diese standardmäßig. Hake ab, was privat bleiben soll.",
      "catMedical": "Krankenakte",
      "catDocuments": "Dokumente",
      "catReminders": "Erinnerungen",
      "catInfo": "Details (Rasse, Alter…)",
      "yourYard": "Dein Stall",
      "stableLocation": "Wo im Stall",
      "stableLocationPlaceholder": "z. B. Box 4, Westkoppel",
      "peopleHeading": "Wer ist im Stall",
      "peopleHint": "Andere, die hier einstellen oder arbeiten."
    },
    "accept": {
      "title": "Tritt {farm} auf HorseSheli bei",
      "heading": "Tritt einem Stall auf HorseSheli bei",
      "invitedYou": "{farm} hat dich eingeladen",
      "body": "Nimm an, um dich mit dem Stall zu verbinden. Du behältst volle Kontrolle über deine Pferde und kannst alles verbergen.",
      "cta": "Einladung annehmen",
      "busy": "Beitreten…",
      "move": "Hierher wechseln",
      "moveNote": "Du bist bereits in einem Stall — Annehmen verlegt dich (und deine Pferde) hierher.",
      "signin": "Melde dich an oder erstelle ein kostenloses Konto, um anzunehmen.",
      "doneTitle": "Du bist {farm} beigetreten 🎉",
      "doneBody": "Stelle ein Pferd ein, um es mit dem Stall zu teilen.",
      "invalid": "Einladung nicht gefunden",
      "invalidBody": "Dieser Link wurde evtl. zurückgezogen oder bereits benutzt.",
      "goApp": "Zu meinen Pferden"
    },
    "switchHorses": "Meine Pferde",
    "switchFarm": "Mein Stall",
    "roles": {
      "owner": "Stallbesitzer",
      "manager": "Verwalter",
      "groom": "Pfleger / Personal",
      "boarder": "Einsteller (Pferdebesitzer)"
    },
    "packages": {
      "full": "Vollpension",
      "part": "Teilpension",
      "diy": "Selbstversorger",
      "grass": "Weidepension",
      "schooling": "Ausbildungspension",
      "retirement": "Gnadenbrot",
      "other": "Sonstiges",
      "label": "Pensionsart",
      "none": "Keine Art gewählt",
      "prices": "Preise der Pensionsarten",
      "pricesHint": "Monatspreis für jede angebotene Art — wird zum automatischen Erstellen von Rechnungen genutzt. Nicht angebotene Arten leer lassen.",
      "perMonthShort": "/Mon."
    },
    "tasks": {
      "title": "Tägliche Aufgaben",
      "today": "Heute",
      "add": "+ Aufgabe hinzufügen",
      "addTitle": "Aufgabe hinzufügen",
      "taskName": "Was ist zu tun?",
      "taskPlaceholder": "z. B. Weidepferde reinholen, Abendfütterung",
      "notes": "Notizen (optional)",
      "forHorse": "Für ein Pferd (optional)",
      "wholeYard": "Ganzer Stall",
      "assignTo": "Zuweisen an (optional)",
      "anyone": "Alle",
      "empty": "Keine Aufgaben an diesem Tag",
      "emptyBody": "Trage die Aufgaben des Tages ein — Fütterung, Weidegang, Decken, Medikamente — und das Team hakt sie ab.",
      "copyYesterday": "Gestrige Liste kopieren",
      "copied": "{n} Aufgaben kopiert",
      "done": "Erledigt",
      "todo": "Zu tun",
      "doneBy": "Erledigt von {name}",
      "progress": "{done} von {total} erledigt",
      "allDone": "Alles für heute erledigt 🎉",
      "deleteTask": "Aufgabe löschen"
    },
    "members": {
      "title": "Personen im Stall",
      "staff": "Personal",
      "liveries": "Einsteller",
      "horsesHere": "{n} Pferd(e) hier",
      "joined": "Beigetreten {date}",
      "role": "Rolle",
      "changeRole": "Rolle ändern",
      "remove": "Aus dem Stall entfernen",
      "removeConfirm": "{name} aus dem Stall entfernen? Ihre Pferde verlassen den Stall ebenfalls.",
      "empty": "Noch niemand beigetreten",
      "emptyBody": "Lade deine Einsteller und dein Personal ein — sie erscheinen hier, sobald sie annehmen."
    },
    "invoices": {
      "title": "Rechnungen",
      "myTitle": "Meine Rechnungen",
      "raise": "+ Neue Rechnung",
      "raiseTitle": "Neue Rechnung",
      "billTo": "Rechnung an",
      "autoFill": "Aus Pensionsarten erstellen",
      "autoFillHint": "Fügt für jedes ihrer Pferde eine Zeile anhand deiner Preise hinzu.",
      "period": "Zeitraum (z. B. August 2026)",
      "dueDate": "Fälligkeitsdatum",
      "notes": "Notizen (optional)",
      "line": "Position",
      "lineDesc": "Beschreibung",
      "amount": "Betrag (£)",
      "addLine": "+ Position hinzufügen",
      "total": "Gesamt",
      "create": "Entwurf erstellen",
      "creating": "Wird erstellt…",
      "send": "An {name} senden",
      "sendShort": "Senden",
      "markPaid": "Als bezahlt markieren",
      "voidIt": "Stornieren",
      "edit": "Bearbeiten",
      "empty": "Noch keine Rechnungen",
      "emptyBody": "Lege deine Pensionspreise fest und erstelle deine erste Rechnung mit zwei Tipps.",
      "emptyMine": "Noch keine Rechnungen — wenn dein Stall dir eine stellt, erscheint sie hier.",
      "st": {
        "draft": "Entwurf",
        "sent": "Gesendet",
        "paid": "Bezahlt",
        "void": "Storniert"
      },
      "payNote": "Zahle per Überweisung oder wie dein Stall es bevorzugt — er markiert sie als bezahlt.",
      "sentOn": "Gesendet {date}",
      "paidOn": "Bezahlt {date}",
      "due": "Fällig {date}",
      "noPrices": "Lege zuerst deine Pensionspreise fest (Stall-Details) oder füge Positionen manuell hinzu."
    }
  },
  "tickets": {
    "title": "Anliegen",
    "dashTitle": "Anliegen der Einsteller",
    "open": "Offen",
    "resolved": "Gelöst",
    "all": "Alle",
    "openCount": "{n} offen",
    "raise": "Anliegen melden",
    "noneOwner": "Noch keine Anliegen — sobald ein Einsteller eines meldet, erscheint es hier.",
    "noneMine": "Du hast noch keine Anliegen gemeldet.",
    "viewAll": "Alle Anliegen ansehen",
    "subject": "Betreff",
    "subjectPlaceholder": "z. B. Extra Heu für Comet",
    "message": "Details",
    "messagePlaceholder": "Was brauchst du?",
    "yard": "Stall",
    "category": "Kategorie",
    "priority": "Priorität",
    "horse": "Zu einem Pferd",
    "horseNone": "Allgemein (nicht pferdebezogen)",
    "submit": "An den Stall senden",
    "sending": "Senden…",
    "markResolved": "Als gelöst markieren",
    "reopen": "Wieder öffnen",
    "by": "Von",
    "about": "zu",
    "cat": {
      "general": "Allgemein",
      "care": "Pflege & Futter",
      "facilities": "Anlage",
      "health": "Gesundheit",
      "billing": "Abrechnung"
    },
    "prio": {
      "normal": "Normal",
      "urgent": "Dringend"
    },
    "st": {
      "open": "Offen",
      "resolved": "Gelöst"
    }
  },
  "footer": {
    "tagline": "Eine ruhigere Art, jedes Pferd — und jeden Stall — organisiert zu halten.",
    "privacy": "Datenschutz",
    "terms": "AGB"
  },
  "crash": {
    "title": "Etwas ist schiefgelaufen",
    "body": "Diese Seite hatte ein Problem. Versuche es erneut oder lade neu — deine Daten sind sicher.",
    "retry": "Erneut versuchen",
    "reload": "Neu laden"
  },
  "update": {
    "available": "Eine neue Version ist verfügbar",
    "reload": "Neu laden",
    "later": "Schließen"
  },
  "units": {
    "yrs": "J.",
    "hh": "hh"
  },
  "common": {
    "save": "Speichern",
    "delete": "Löschen",
    "cancel": "Abbrechen",
    "close": "Schließen"
  },
  "confirm": {
    "deleteTitle": "Dies löschen?",
    "deleteBody": "Das kann nicht rückgängig gemacht werden."
  },
  "errors": {
    "rateLimited": "Zu viele Anfragen — bitte warte kurz und versuche es erneut.",
    "inviteEmailMismatch": "Diese Einladung wurde an eine andere E-Mail-Adresse gesendet. Melde dich mit der eingeladenen E-Mail an, um anzunehmen.",
    "invalidInput": "Das sah nicht richtig aus — bitte prüfe das Formular und versuche es erneut.",
    "generic": "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
    "trialExpired": "Deine Testphase ist abgelaufen — abonniere, um weiterzumachen.",
    "imageRejected": "Dieses Bild wurde von unserer Inhaltsprüfung blockiert.",
    "quota": "Du hast dein Speicherlimit erreicht.",
    "docLimit": "Du hast das Dokumentlimit für dieses Pferd erreicht.",
    "horseLimit": "Du hast das Pferdelimit deines Tarifs erreicht.",
    "notMember": "Du musst diesem Stall zuerst beitreten.",
    "unauthorized": "Bitte melde dich erneut an.",
    "required": "Bitte ausfüllen: {fields}"
  },
  "gender": {
    "Mare": "Stute",
    "Stallion": "Hengst",
    "Gelding": "Wallach"
  },
  "status": {
    "active": "aktiv",
    "sold": "verkauft",
    "retired": "im Ruhestand",
    "deceased": "verstorben"
  },
  "colour": {
    "Bay": "Brauner",
    "Dark Bay": "Dunkelbrauner",
    "Chestnut": "Fuchs",
    "Grey": "Schimmel",
    "Dun": "Falbe"
  },
  "discipline": {
    "Eventing": "Vielseitigkeit",
    "Dressage": "Dressur",
    "Showing": "Showing"
  },
  "medicalCategory": {
    "vaccination": "Impfung",
    "deworming": "Wurmkur",
    "dental": "Zahnpflege",
    "farrier": "Hufschmied",
    "vet_exam": "Tierarzt-Check",
    "bloodwork": "Blutbild",
    "injury": "Verletzung",
    "medication": "Medikament",
    "allergy": "Allergie",
    "weight_condition": "Gewicht & Zustand",
    "other": "Sonstiges"
  },
  "landing": {
    "heroBadge": "🐎 Für Pferdebesitzer, von Pferdemenschen",
    "heroTitleA": "Das ganze Leben deines Pferdes,",
    "heroTitleB": "wunderschön organisiert.",
    "heroSubtitle": "Aufzeichnungen, Dokumente, Krankengeschichte und Erinnerungen für jedes deiner Pferde — standardmäßig privat, teilbar wann du willst. Keine Tabellen. Keine Zettelkästen.",
    "getStarted": "Kostenlos testen →",
    "seePublic": "Pferde entdecken",
    "twoDoor": {
      "title": "Zwei Wege hinein",
      "horseTitle": "Ich bin Pferdebesitzer",
      "horseBody": "Profil, Fotos, Krankenakte und Erinnerungen deines Pferdes an einem ruhigen Ort. Kostenlos starten.",
      "horseCta": "Kostenlos starten →",
      "farmTitle": "Ich führe einen Stall",
      "farmBody": "Sieh alle Pferde im Stall auf einen Blick — Überblick, Medizin, Erinnerungen — und lade deine Einsteller ein. 3 Monate gratis.",
      "farmCta": "Stall starten →"
    },
    "trialNote": "3 Monate kostenlos — keine Karte nötig zum Start.",
    "features": {
      "title": "Alles, was ein Pferd braucht — ohne den Papierkram",
      "subtitle": "HorseSheli ist in erster Linie ein Verwaltungswerkzeug. Ruhig, klar und so gebaut, dass auch nicht-technische Besitzer es ohne Anleitung nutzen können.",
      "items": [
        {
          "title": "Alle Daten an einem Ort",
          "body": "Infos, Fotos, Dokumente, Krankengeschichte, News — jedes Pferd in übersichtlichen Bereichen, in einem Tipp erreichbar."
        },
        {
          "title": "Nie wieder einen Termin verpassen",
          "body": "Hufschmied, Wurmkur, Impfungen, Zahnpflege — sanfte E-Mail-Erinnerungen, bevor etwas fällig wird."
        },
        {
          "title": "Du entscheidest, was geteilt wird",
          "body": "Profile sind standardmäßig privat. Mach ein Pferd — oder nur seine Fotos — öffentlich, Bereich für Bereich."
        },
        {
          "title": "Dokumente, die nie verloren gehen",
          "body": "Pass, Registrierung, Versicherung, Zertifikate — einmal hochgeladen, für immer sicher."
        },
        {
          "title": "Eine Krankengeschichte, die mitreist",
          "body": "Impfungen, Behandlungen, Tierarztbesuche in einer Zeitleiste. Teile sie in Sekunden mit dem Tierarzt."
        },
        {
          "title": "Verbinden statt scrollen",
          "body": "Folge Pferden die dir gefallen, teile einen Profillink, sieh ihre News. Ein Netzwerk — ohne den Lärm eines Feeds."
        }
      ]
    },
    "how": {
      "title": "In wenigen Minuten startklar",
      "steps": [
        {
          "title": "Pferd hinzufügen",
          "body": "Name, Foto, Rasse, das Wichtigste. Dauert eine Minute."
        },
        {
          "title": "Das Leben eintragen",
          "body": "Dokumente hochladen, Gesundheit und Pflege festhalten. Oder Stück für Stück."
        },
        {
          "title": "Teilen, wenn du willst",
          "body": "Privat halten oder ein schönes Profil zum Teilen veröffentlichen."
        }
      ]
    },
    "privacy": {
      "badge": "🔒 Privatsphäre ist Standard",
      "title": "Privat, bis du etwas anderes sagst",
      "body": "Krankenakten, Dokumente und Erinnerungen sind privat — Punkt. Fotos, Basisdaten und News kannst du Bereich für Bereich öffentlich machen — so teilst du genau das, was du willst.",
      "bullets": [
        "Jedes Pferd: öffentlich oder privat",
        "Jeder Bereich: unabhängig steuerbar",
        "Medizin & Dokumente: immer privat",
        "Teile einen sauberen öffentlichen Link mit einem Tipp"
      ]
    },
    "cta": {
      "title": "Schau dich um",
      "body": "Starte deine kostenlose 3-Monats-Testphase und sag uns, was sie für deinen Stall wirklich nützlich machen würde.",
      "button": "Kostenlos testen →"
    }
  },
  "auth": {
    "magic": {"or": "oder", "cta": "Anmeldelink per E-Mail", "sent": "Sieh in deinem Postfach nach — wir haben dir einen Link geschickt, der dich direkt anmeldet. Er gilt 15 Minuten.", "needEmail": "Gib zuerst deine E-Mail-Adresse ein.", "failedTitle": "Der Link hat nicht funktioniert", "expired": "Anmeldelinks gelten 15 Minuten und funktionieren einmal. Fordere einfach einen neuen an.", "badLink": "Der Link sieht unvollständig aus. Fordere unten einen neuen an.", "tryAgain": "Neuen Link senden"},
    "email": "E-Mail",
    "password": "Passwort",
    "newPassword": "Neues Passwort",
    "agreePre": "Mit dem Erstellen eines Kontos stimmst du den",
    "agreeTerms": "Nutzungsbedingungen",
    "agreeAnd": "und der",
    "agreePrivacy": "Datenschutzerklärung",
    "agreePost": " zu.",
    "chooseRole": "Zuerst das Wichtigste — wie wirst du HorseSheli nutzen?",
    "subtitleHorse": "Kostenlos für die Daten, Fotos und Erinnerungen deiner Pferde.",
    "subtitleFarm": "Starte die 3-monatige Gratis-Testphase deines Stalls — ohne Karte.",
    "roleHorseDesc": "Die Daten, Krankenakte und Erinnerungen meiner Pferde — und ihr Stall.",
    "roleFarmDesc": "Den Stall führen: Einsteller und Personal, tägliche Aufgaben und Rechnungen.",
    "code": "Bestätigungscode",
    "passwordHint": "Mindestens 10 Zeichen, mit Groß- und Kleinbuchstaben und einer Zahl.",
    "noAccount": "Noch kein Konto?",
    "haveAccount": "Schon ein Konto?",
    "forgotLink": "Passwort vergessen?",
    "resend": "Code erneut senden",
    "backToSignin": "Zurück zur Anmeldung",
    "backHome": "Zur Startseite",
    "signin": {
      "title": "Willkommen zurück",
      "subtitle": "Melde dich bei deinem HorseSheli-Konto an.",
      "cta": "Anmelden"
    },
    "signup": {
      "title": "Konto erstellen",
      "subtitle": "Starte deine kostenlose 3-Monats-Testphase.",
      "cta": "Registrieren"
    },
    "confirm": {
      "title": "Prüfe deine E-Mail",
      "subtitle": "Gib den Code ein, den wir gerade gesendet haben.",
      "cta": "Bestätigen"
    },
    "forgot": {
      "title": "Passwort zurücksetzen",
      "subtitle": "Wir senden dir einen Code per E-Mail.",
      "cta": "Code senden"
    },
    "reset": {
      "title": "Neues Passwort wählen",
      "subtitle": "Gib den Code und dein neues Passwort ein.",
      "cta": "Passwort zurücksetzen"
    }
  },
  "onboarding": {
    "badge": "Willkommen",
    "title": "Richte dein Profil ein",
    "subtitle": "Ein paar Angaben, damit andere Besitzer wissen, wer du bist. Du kannst sie jederzeit ändern.",
    "addPhoto": "Foto hinzufügen",
    "name": "Dein Name",
    "about": "Über dich",
    "aboutPlaceholder": "Reiterin seit jeher, Amateur-Dressur…",
    "country": "Land",
    "city": "Stadt",
    "memory": "Eine liebste Pferde-Erinnerung",
    "memoryPlaceholder": "Das erste Pony, das du geritten hast…",
    "cta": "Weiter zu meinen Pferden →"
  },
  "dashboard": {
    "trialBanner": "Du bist in der kostenlosen Testphase — noch {days} Tage.",
    "trialCta": "Abo verwalten",
    "welcome": "Willkommen zurück, {name}",
    "addHorse": "+ Pferd hinzufügen",
    "stats": {
      "horses": "Pferde",
      "public": "Öffentliche Profile",
      "storage": "Speicher belegt"
    },
    "myHorses": "Meine Pferde",
    "comingSoon": "Mehr Pferde — demnächst",
    "empty": {
      "title": "Noch keine Pferde",
      "body": "Füge dein erstes Pferd hinzu, um Profil, Krankenbuch und mehr aufzubauen."
    }
  },
  "horse": {
    "access": {"title": "Personen mit Zugriff", "body": "Gib jemandem einen eigenen Zugang zu diesem Pferd. Miteigentümer können Einträge hinzufügen; Betrachter nur ansehen.", "none": "Noch hat niemand sonst Zugriff.", "pending": "Einladung gesendet — wartet auf Annahme", "email": "Ihre E-Mail", "emailPlaceholder": "name@beispiel.de", "roleLabel": "Zugriff", "invite": "Einladen", "inviting": "Senden…", "remove": "Entfernen", "cancel": "Abbrechen", "full": "Ein Pferd kann mit bis zu {n} Personen geteilt werden.", "note": "Du bleibst Eigentümer: nur du kannst das Pferd zwischen Ställen bewegen, die Öffentlichkeit ändern oder es löschen.", "role": {"coOwner": "Miteigentümer", "viewer": "Betrachter"}, "badgeCoOwner": "Geteilt von {owner}", "badgeViewer": "Geteilt von {owner} · nur Ansicht"},
    "share": {"accepted": "Du hast jetzt Zugriff auf {horse}", "acceptedBody": "Es steht ab sofort in deiner Pferdeliste.", "view": "Pferd öffnen", "failed": "Die Einladung hat nicht funktioniert", "failedBody": "Sie wurde möglicherweise zurückgezogen, bereits verwendet oder an eine andere E-Mail-Adresse gesendet.", "backHome": "Zurück zu meinen Pferden"},
    "back": "Meine Pferde",
    "public": "🌍 Öffentlich",
    "private": "🔒 Privat",
    "viewPublic": "Öffentliche Seite ansehen",
    "followers": "Follower",
    "notFound": "Pferd nicht gefunden",
    "notFoundBack": "Zurück zu meinen Pferden",
    "sections": {
      "info": "Informationen",
      "photos": "Fotos",
      "news": "News",
      "medical": "Medizin",
      "reminders": "Erinnerungen",
      "documents": "Dokumente",
      "sharing": "Teilen",
      "pedigree": "Abstammung"
    },
    "about": "Über",
    "noAbout": "Noch keine Beschreibung.",
    "details": "Details",
    "edit": "Bearbeiten",
    "editHorse": "Pferd bearbeiten",
    "deleteHorse": "Dieses Pferd löschen",
    "deleteConfirm": "Dieses Pferd und alle Einträge löschen? Das kann nicht rückgängig gemacht werden.",
    "fields": {
      "name": "Name",
      "dob": "Geburtsdatum",
      "breed": "Rasse",
      "gender": "Geschlecht",
      "colour": "Farbe",
      "height": "Größe (hh)",
      "discipline": "Disziplin",
      "status": "Status",
      "microchip": "Mikrochip",
      "passport": "Pass",
      "country": "Land",
      "city": "Stadt",
      "location": "Standort",
      "category": "Kategorie",
      "title": "Titel",
      "date": "Datum",
      "vet": "Tierarzt / Behandler",
      "notes": "Notizen",
      "interval": "Wiederholen alle",
      "nextDue": "Nächste Fälligkeit"
    },
    "gallery": "Fotogalerie",
    "upload": "+ Hochladen",
    "setProfilePhoto": "Hauptfoto festlegen",
    "noPhotos": "Noch keine Fotos. Füge welche hinzu, um das Profil lebendig zu machen.",
    "newsTitle": "News",
    "addNews": "+ News posten",
    "noNews": "Noch keine News. Teile ein Update über dein Pferd.",
    "newsTitleField": "Überschrift",
    "newsBody": "Was gibt’s Neues?",
    "addImages": "Bilder hinzufügen",
    "publish": "Veröffentlichen",
    "medicalTitle": "Krankenbuch — privat",
    "addRecord": "+ Eintrag hinzufügen",
    "noMedical": "Noch keine Einträge. Erfasse eine Impfung, Zahnpflege oder einen Tierarztbesuch.",
    "remindersTitle": "Pflege-Erinnerungen — privat",
    "addReminder": "+ Erinnerung hinzufügen",
    "attachDocs": "Dokumente anhängen",
    "attachHint": "Fotos oder PDFs — Tierarztberichte, Röntgen, Laborwerte.",
    "attachment": "Anhang",
    "remindersHint": "Du bekommst eine E-Mail, wenn etwas fällig ist.",
    "noReminders": "Noch keine Erinnerungen.",
    "next": "Nächste:",
    "everyMonths": "alle {n} Monate",
    "markDone": "Als erledigt markieren",
    "documentsTitle": "Dokumente — privat",
    "noDocuments": "Noch keine Dokumente. Lade Pass, Registrierung oder Versicherung hoch.",
    "download": "Öffnen",
    "sharingTitle": "Teilen & Datenschutz",
    "makePublic": "Öffentliches Profil",
    "makePublicHint": "Lass jeden dieses Pferd über einen Link sehen.",
    "whatPublic": "Was ist öffentlich sichtbar",
    "alwaysPrivate": "Medizin, Erinnerungen und Dokumente sind immer privat — werden nie öffentlich gezeigt.",
    "vis": {
      "public": "Öffentlich",
      "private": "Privat",
      "country": "Nur Land",
      "city": "Land + Stadt"
    }
  },
  "public": {
    "badge": "🌍 Öffentliches Profil",
    "share": "🔗 Link teilen",
    "linkCopied": "Link kopiert!",
    "follow": "+ Folgen",
    "following": "✓ Folge ich",
    "signInToFollow": "Anmelden zum Folgen",
    "photos": "Fotos",
    "latestNews": "Neueste News",
    "privateNote": "Krankenakten, Dokumente und Erinnerungen sind privat und werden nie auf öffentlichen Profilen gezeigt.",
    "privateTitle": "Dieses Profil ist privat",
    "privateBody": "Dieses Pferd wird nicht öffentlich geteilt, oder der Link ist falsch.",
    "backDiscover": "Pferde entdecken"
  },
  "discover": {
    "title": "Pferde entdecken",
    "subtitle": "Durchstöbere öffentliche Pferde von Besitzern weltweit.",
    "tabDiscover": "Entdecken",
    "tabFollowing": "Folge ich",
    "filterCountry": "Land",
    "filterBreed": "Rasse",
    "filterDiscipline": "Disziplin",
    "anyGender": "Alle Geschlechter",
    "filterHint": "Tipp: Filter berücksichtigen den Wortanfang (z. B. „Irish“, nicht „sport“).",
    "emptyTitle": "Keine Pferde gefunden",
    "emptyBody": "Entferne einen Filter oder schau bald wieder vorbei.",
    "followingEmptyTitle": "Du folgst noch niemandem",
    "followingEmptyBody": "Folge Pferden die dir gefallen, um hier ihre News zu sehen."
  },
  "billing": {
    "paid": "Zahlung erhalten — danke! Dein Abo wird gleich aktiviert.",
    "cancelled": "Checkout abgebrochen — es wurde nichts abgebucht.",
    "redirecting": "Du wirst zum sicheren Checkout weitergeleitet…",
    "activeTitle": "Alles bereit",
    "activeBody": "Dein Abo ist aktiv. Vielen Dank!",
    "lockedTitle": "Deine Testphase ist beendet",
    "lockedBody": "Füge eine Zahlungsmethode hinzu, um deine Pferde weiter zu verwalten.",
    "trialTitle": "Kostenlose Testphase",
    "trialBody": "Noch {days} Tage — deine Testphase endet am {date}.",
    "month": "Monat",
    "feat1": "Bis zu 3 Pferde",
    "feat2": "Krankenbuch, Erinnerungen & Dokumente",
    "feat3": "Öffentliche Profile & Folgen",
    "subscribe": "Zahlungsmethode hinzufügen",
    "mockNote": "Nur Demo — es wird keine echte Zahlung erhoben.",
    "backToApp": "Zurück zu meinen Pferden",
    "daysLeft": "noch {days} Tage"
  },
  "settings": {
    "title": "Einstellungen",
    "profile": "Dein Profil",
    "changePhoto": "Foto ändern",
    "saved": "Gespeichert",
    "plan": "Abo",
    "currentPlan": "Aktueller Plan",
    "trialEnds": "Testphase endet am {date}",
    "manageBilling": "Abrechnung verwalten",
    "storage": "Speicher",
    "signOut": "Abmelden"
  },
  "legal": {
    "updated": "Zuletzt aktualisiert: {date}"
  },
  "admin": {"title": "Support-Konsole", "subtitle": "Nur-Lese-Übersicht über Ställe und Konten, um Menschen zu helfen, die nicht weiterkommen.", "tab": {"yards": "Ställe", "users": "Konten"}, "stat": {"users": "Konten", "yards": "Ställe", "horses": "Pferde", "boardings": "Eingestellt", "shares": "Geteilte Pferde", "invites": "Offene Einladungen"}, "members": "{n} Personen", "horses": "{n} Pferde", "owner": "Eigentümer", "people": "Personen im Stall", "boardedHorses": "Eingestellte Pferde", "invites": "Einladungen", "theirHorses": "Ihre Pferde", "ownedYards": "Eigene Ställe", "memberOf": "Mitglied bei", "created": "Erstellt {date}", "none": "Keine", "onboarded": "Eingerichtet", "notOnboarded": "Einrichtung nicht abgeschlossen", "privacyNote": "Krankenakten, Dokumente und Fotos werden hier nie angezeigt — sie bleiben zwischen Eigentümer und Stall."},
  "notFound": {
    "title": "Seite nicht gefunden",
    "back": "Zur Startseite"
  },
  "authErrors": {
    "incorrect": "E-Mail oder Passwort stimmt nicht — bitte versuche es erneut.",
    "notConfirmed": "Deine E-Mail ist noch nicht bestätigt — sieh in deinem Postfach nach dem Code.",
    "exists": "Es gibt bereits ein Konto mit dieser E-Mail. Melde dich stattdessen an.",
    "policy": "Passwörter brauchen mindestens 10 Zeichen mit einem Groß-, einem Kleinbuchstaben und einer Zahl.",
    "codeMismatch": "Der Code stimmt nicht — prüfe die E-Mail und versuche es erneut.",
    "expired": "Der Code ist abgelaufen — wir können dir einen neuen senden.",
    "attempts": "Zu viele Versuche — warte ein paar Minuten und versuche es erneut.",
    "notFound": "Wir finden kein Konto mit dieser E-Mail.",
    "captcha": "Die Sicherheitsprüfung ist fehlgeschlagen — bitte versuche es erneut.",
    "generic": "Das hat nicht geklappt — prüfe deine Angaben und versuche es erneut."
  }
}

const DICTS: Record<Lang, Dict> = { en, da, de }

function lookup(lang: Lang, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[key]
    return undefined
  }, DICTS[lang])
}

function interpolate(s: string, vars?: Record<string, string | number>): string {
  if (!vars) return s
  return s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`))
}

interface I18nValue {
  lang: Lang
  setLang: (l: Lang) => void
  locale: string
  t: (path: string, vars?: Record<string, string | number>) => string
  td: (namespace: string, value: string) => string
  tArr: (path: string) => any[]
}

const I18nContext = createContext<I18nValue | null>(null)

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    const saved = localStorage.getItem('hh-lang') as Lang | null
    if (saved && DICTS[saved]) return saved
    const nav = navigator.language.slice(0, 2)
    return nav === 'da' || nav === 'de' ? (nav as Lang) : 'en'
  })

  useEffect(() => {
    localStorage.setItem('hh-lang', lang)
    document.documentElement.lang = lang
  }, [lang])

  const value: I18nValue = {
    lang,
    setLang: setLangState,
    locale: LOCALES[lang],
    t: (path, vars) => {
      const r = lookup(lang, path) ?? lookup('en', path)
      return typeof r === 'string' ? interpolate(r, vars) : path
    },
    td: (namespace, val) => {
      const r = lookup(lang, `${namespace}.${val}`) ?? lookup('en', `${namespace}.${val}`)
      return typeof r === 'string' ? r : val
    },
    tArr: (path) => {
      const r = lookup(lang, path) ?? lookup('en', path)
      return Array.isArray(r) ? r : []
    },
  }

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used within I18nProvider')
  return ctx
}
